import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import * as moment from 'moment';
import { SharedService } from 'src/app/shared/shared.service';
import { LoaderService } from 'src/app/loader.service';
import { ToasterService } from 'src/app/toaster.service';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/auth.service';
import { environment } from 'src/environments/environment';
import Utils from 'src/app/utils/utils';
import {
  SLAB_APPROVAL_PREVIEW_KEY_PREFIX,
  SlabApprovalPreviewContext,
  SlabApprovalRequestsService,
} from '../slab-approval-requests/slab-approval-requests.service';
import { AlertDialogService } from '../shared/alert-dialog/alert-dialog.service';
import { AUTHZ_FEATURES } from '../constants/permissions';
import { permissionsForFeature } from '../auth.service';
import { AuthzAccessHintService } from '../shared/authz-access-hint/authz-access-hint.service';

interface MinimumGuaranteeSlot {
  startTime: string;
  endTime: string;
}

interface MinimumGuaranteeDetails {
  eligibilityCriteria: {
    orders: number | null;
  };
  minimumGuaranteeAmount: number | null;
  perceptionAmount: number | null;
  microCorridorId?: string | null;
  slots: MinimumGuaranteeSlot[];
}

interface MicroCorridorOption {
  id: string;
  name: string;
  zoneId: string;
  startTime: string;
  endTime: string;
}

@Component({
  selector: 'app-create-slab',
  templateUrl: './create-slab.component.html',
  styleUrls: ['./create-slab.component.css']
})
export class CreateSlabComponent implements OnInit, OnDestroy {

  public authzAccessHint: { message: string; requestUrl: string } | null = null;
  public actionTitle = 'Add';
  public isViewMode = false;
  public isApprovalPreview = false;
  public approvalRequestId: string;
  public approvalRequestStatus = '';
  public approvalRequestStatusDisplay = '';
  public approvalRequestCreatedBy = '';
  public approvalRequestApprovedBy = '';
  public isApproved = false;
  public canShowApprovalActions = false;
  public canApprove = false;
  public canReject = false;
  public processingApprovalRequest = false;
  public approvalActionMessage = '';
  private approvalPreviewContext: SlabApprovalPreviewContext;
  private currentUserEmail = '';
  private hasApprovePermission = false;
  private hasCreatePermission = false;
  public isGeoSlabPage = false;
  public loadingIndicator = false;
  public showConfirmationModal = false;
  public existingShiftSearch = '';
  public existingServiceSearch = '';

  public cities: any[] = [];
  public services: any[] = [];
  private allServices: any[] = [];
  public shifts: string[] = [];
  public serviceNames: { [id: string]: string } = {};
  public serviceMap: { [id: string]: any } = {};
  public earningModels: string[] = [];
  public dashboardConfig: any = null;
  private duplicateSlabConfigBlockHandled = false;
  public mode: string | null = null;
  public modes: { id: string, name: string }[] = [];
  public selectedMode: string | null = null;
  public selectedModeNames: string[] = [];
  private oldApiShifts: string[] = [];
  private newApiShifts: string[] = [];

  // Use configuration from server config
  public isFetchingShiftsFromSlabEnabled: boolean = environment.isFetchingShiftsFromCaptainEarningsEnabled || false;

  public categories = [];
  public batchedFirstOrderCategories = ['order'];
  public batchedNextOrderCategories = ['order'];
  public daysList = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  public timeBasedSlabRuleTypes = ['percentage'];
  public shiftSelection: 'existing' | 'new' = 'existing';
  public isFallbackSlab = false;
  /** True when default slab is on, city & mode are selected, and API returned no default shifts */
  public defaultShiftsFetchedAndEmpty = false;
  public category: string | null = null;
  public selectedCities: string | null = null;
  public selectedServiceNames: string[] = [];
  public layers: { id: string, name: string }[] = [];
  public selectedLayer: string | null = null;
  public selectedGeoLayerType = 'hotspot';
  public readonly geoLayerTypes: { id: string, name: string }[] = [
    { id: 'hotspot', name: 'Hotspot' } //for now, only hotspot is supported
  ];
  public geoFenceRadius: number | null = null;
  public geoFenceAmount: number | null = null;
  public layersLoading = false;
  public layersError = false;
  public existingGeoSlab: any = null;
  public existingGeoSlabs: any[] = [];
  public existingGeoSlabsLoading = false;
  public existingGeoSlabLookupCount = 0;
  public readonly layersErrorMessage = 'Could not load layers. Please retry.';
  /**
   * From dashboard `minimumGuaranteeSlotCount`: maximum MG time slots allowed (≥1).
   * Users must keep at least one slot; they may use any count from 1 up to this maximum.
   */
  public minimumGuaranteeSlotCount = 1;
  public readonly minimumGuaranteeBannerMessage =
    'A slab with Minimum Guarantee already exists for this shift. You cannot modify these values. Please create a new shift to define different MG values.';
  public isMinimumGuaranteeReadOnly = false;
  public showMinimumGuaranteeBanner = false;
  /** While `/api/get/slabs` is in flight for the current MG (city/shift) context. */
  public minimumGuaranteeSlabsLookupPending = false;
  /** Slabs API failed for the current context; use Retry to refetch. */
  public minimumGuaranteeSlabsLookupError = false;
  public readonly minimumGuaranteeSlabsLookupErrorMessage =
    'Could not load MG details from existing slabs. Please retry slab creation from the slab creation screen, or use Retry below.';
  public microCorridors: MicroCorridorOption[] = [];
  public selectedMicroCorridorIds: string[] = [];
  public microCorridorsLoading = false;
  public microCorridorsError = false;
  public microCorridorsErrorCanRetry = true;
  public readonly defaultMicroCorridorsErrorMessage = 'Could not load MicroCorridors. Please retry.';
  public readonly microCorridorsEmptyErrorMessage =
    'No MicroCorridors found. Please create a MicroCorridor for the above service.';
  public microCorridorsErrorMessage = this.defaultMicroCorridorsErrorMessage;
  public minimumGuaranteeShiftRequiresMicroCorridorId = false;

  public flatCommissionDeductionsLoading = false;
  public flatCommissionDeductionsError = false;
  public flatCommissionTax: number | null = null;
  public flatCommissionPlatformFee: number | null = null;
  public flatCommissionDeductionDetails: { serviceDetailId: string, tax: number, platformFee: number }[] = [];
  private flatCommissionDeductionsRequestId = 0;
  private geoLayersRequestId = 0;
  private existingGeoSlabLookupId = 0;
  public readonly fetchTaxesAndOrPlatformFeeErrorMessage =
    'Could not load tax and platform fee details. Please retry.';
  public readonly flatCommissionInclusiveAmountNote =
    'This commission amount is inclusive of rapido taxes (excluding captain taxes) and platform fees.';
  public slabHasCommissionWithAdditionalDeductions = false;

  public BatchedFirstOrderCategory: string | null = null;
  public BatchedNextOrderCategory: string | null = null;
  public isBatchedOrder = false;

  // First/Last mile
  public thresholdDistanceFM: number | null = null;
  public thresholdFareFM: number | null = null;
  public maxCompensationFM: number | null = null;
  public minCompensationFM: number | null = null;
  public thresholdDistanceLM: number | null = null;
  public thresholdFareLM: number | null = null;

  public desiredCustomerPpkm: number | null = null;
  public canSetDesiredCustomerPpkm = false;

  // Time-based slabs (distance category)
  public timeBasedSlabs: any[] = [];

  public params: any = {};

  @ViewChild('startDateInput') startDateInput: ElementRef<HTMLInputElement> | null = null;

  private finalSlabData: any = {};
  private persistValues: any = {};
  private minimumGuaranteeLookupCache: {
    [contextKey: string]: {
      hasConstruct: boolean,
      details: MinimumGuaranteeDetails | null,
      shiftRequiresMicroCorridorIdForMg: boolean
    }
  } = {};
  private minimumGuaranteeLookupInFlight: { [contextKey: string]: boolean } = {};
  private minimumGuaranteeBannerDismissedByContext: { [contextKey: string]: boolean } = {};
  private currentMinimumGuaranteeContextKey: string | null = null;
  private currentMicroCorridorContextKey: string | null = null;
  private microCorridorLookupCache: { [contextKey: string]: MicroCorridorOption[] } = {};
  private microCorridorLookupInFlight: { [contextKey: string]: boolean } = {};
  private minimumGuaranteeLoaderVisible = false;
  private readonly minimumGuaranteeLoaderTaskId = 'create-slab-minimum-guarantee';

  constructor(
    private readonly sharedService: SharedService,
    private readonly loader: LoaderService,
    public readonly toasterService: ToasterService,
    public readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly slabApprovalRequestsService: SlabApprovalRequestsService,
    private readonly alertDialogService: AlertDialogService,
    private readonly authzAccessHintService: AuthzAccessHintService,
  ) {}

  ngOnInit() {
    this.isGeoSlabPage = !!(this.route.snapshot.data && this.route.snapshot.data['geoSlab']);
    this.loadAccessHint();
    this.initDesiredCustomerPpkmPermission();
    this.initParams();
    const approvalRequestId = this.route.snapshot.paramMap.get('requestId');
    if (approvalRequestId) {
      this.isApprovalPreview = true;
      this.approvalRequestId = approvalRequestId;
      this.isViewMode = true;
      this.actionTitle = 'View';
      this.fetchCities().then(() => this.loadApprovalPreviewFromConfigManager(approvalRequestId));
      this.fetchDashboardConfig();
      return;
    }
    const slabId = this.route.snapshot.paramMap.get('id');
    const isDuplicate = this.route.snapshot.url.some(function(seg) { return seg.path === 'duplicate'; });

    if (slabId && isDuplicate) {
      this.isViewMode = false;
      this.actionTitle = 'Duplicate';
      this.fetchCities().then(() => this.fetchSlabAndMap(slabId));
      this.fetchDashboardConfig();
      return;
    }
    if (slabId) {
      this.isViewMode = true;
      this.actionTitle = 'View';
      this.fetchCities().then(() => this.fetchSlabAndMap(slabId));
      this.fetchDashboardConfig();
      return;
    }
    this.fetchCities().then(() => this.applyLaunchQueryPrefill());
    this.fetchDashboardConfig();
  }

  private async loadAccessHint(): Promise<void> {
    this.authzAccessHint = await this.authzAccessHintService.getAccessHintData('slab');
  }

  /**
   * Prefill create form from CSCC city-launch query params:
   * CSCC sends city/service ids; display names are resolved locally.
   */
  private applyLaunchQueryPrefill(): void {
    const q = this.route.snapshot.queryParams || {};
    const cityId = (q['city'] || '').trim();
    const modeId = (q['mode'] || '').trim();
    const serviceTypes = String(q['serviceTypes'] || '')
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean);

    if (!cityId) {
      return;
    }

    const city = this.cities.find((c: any) => String(c._id) === String(cityId));
    if (!city) {
      return;
    }

    this.selectedCities = city.displayName;
    this.clearFlatCommissionDeductions();
    this.defaultShiftsFetchedAndEmpty = false;
    this.shifts = [];
    this.params.shift = [];
    this.oldApiShifts = [];
    this.newApiShifts = [];
    this.selectedMode = null;
    this.selectedModeNames = [];
    this.modes = [];
    this.services = [];
    this.allServices = [];
    this.params.serviceTypes = [];

    if (!this.isFetchingShiftsFromSlabEnabled) {
      this.fetchNewShifts(city.displayName);
    }

    this.getCityServices(city.displayName, () => {
      this.sharedService.fetchModes(city._id).toPromise()
        .then((res: any) => {
          this.modes = (res && res.data && res.data.modes) || [];

          if (!modeId) {
            this.showError('Mode is required in launch URL.');
            return;
          }
          const matchedMode = this.modes.find((m: any) => String(m.id) === String(modeId));
          if (!matchedMode) {
            this.showError('Invalid mode in launch URL for the selected city.');
            return;
          }
          this.selectedMode = String(matchedMode.id);
          this.filterServicesByMode();

          if (!serviceTypes.length) {
            this.showError('serviceTypes is required in launch URL.');
            return;
          }
          const unknownServiceTypes = serviceTypes.filter((id: string) =>
            !this.allServices.some((s: any) => String(s._id) === String(id))
          );
          if (unknownServiceTypes.length) {
            this.showError('Invalid serviceTypes in launch URL for the selected city.');
            return;
          }
          const mismatchedModeServiceTypes = serviceTypes.filter((id: string) => {
            const entry = this.serviceMap[id];
            const serviceModeId = entry && entry.mode;
            return !serviceModeId || String(serviceModeId) !== String(this.selectedMode);
          });
          if (mismatchedModeServiceTypes.length) {
            this.showError('serviceTypes do not match the mode in launch URL.');
            return;
          }

          this.params.serviceTypes = serviceTypes.slice();
          this.selectedServiceNames = serviceTypes.map((id: string) => this.serviceNames[id] || id);
          this.servicesChanged();
          this.loadShiftsListForCurrentCityAndMode();
          this.evaluateMinimumGuaranteeState(true);
          this.evaluateFlatCommissionDeductionsState();
        })
        .catch((err: any) => console.error('Launch query mode prefill failed', err));
    });
  }

  ngOnDestroy(): void {
    if (this.minimumGuaranteeLoaderVisible) {
      this.minimumGuaranteeLoaderVisible = false;
      this.loader.closeLoading(this.minimumGuaranteeLoaderTaskId);
    }
  }

  private syncMinimumGuaranteeLoadingOverlay(): void {
    const shouldShow = this.minimumGuaranteeSlabsLookupPending || this.microCorridorsLoading;
    if (shouldShow === this.minimumGuaranteeLoaderVisible) {
      return;
    }
    this.minimumGuaranteeLoaderVisible = shouldShow;
    if (shouldShow) {
      this.loader.openLoading(this.minimumGuaranteeLoaderTaskId);
      return;
    }
    this.loader.closeLoading(this.minimumGuaranteeLoaderTaskId);
  }

  private initParams() {
    this.params = {
      excludeIncentiveTypes: [],
      peakSlab:                    { timeData: [{}], slabData: { slabs: [{}] } },
      peakSlabBatchedFirstOrder:   { timeData: [{}], slabData: { slabs: [{}] } },
      peakSlabBatchedNextOrder:    { timeData: [{}], slabData: { slabs: [{}] } },
      nonPeakSlab:                 { timeData: [{}], slabData: { slabs: [{}] } },
      nonPeakSlabBatchedFirstOrder:{ timeData: [{}], slabData: { slabs: [{}] } },
      nonPeakSlabBatchedNextOrder: { timeData: [{}], slabData: { slabs: [{}] } },
      flexibleSlab:                { timeData: [{}], slabData: { slabs: [{}] } },
      flexibleSlabBatchedFirstOrder:{ timeData: [{}], slabData: { slabs: [{}] } },
      flexibleSlabBatchedNextOrder: { timeData: [{}], slabData: { slabs: [{}] } },
      nightSlab:                   { timeData: [{}], slabData: { slabs: [{}] } },
      nightSlabBatchedFirstOrder:  { timeData: [{}], slabData: { slabs: [{}] } },
      nightSlabBatchedNextOrder:   { timeData: [{}], slabData: { slabs: [{}] } },
      calculationType: 'daily',
      isSalaried: false,
      isFallbackSlab: false,
      payrollVersion: 2.0,
      poolSize: null,
      poolMultipliers: {},
    };
  }

  private fetchCities(): Promise<void> {
    this.loader.openLoading();
    return this.sharedService.fetchCities().toPromise()
      .then((res: any) => {
        this.cities = (res.data.cities || [])
          .filter((c: any) => c.active)
          .map((c: any) => ({
            _id: c._id,
            displayName: c.displayName,
            shortName: c.shortName
          }));
      })
      .catch(() => this.showError('Error fetching cities'))
      .finally(() => this.loader.closeLoading()) as Promise<void>;
  }

  private loadApprovalPreviewFromConfigManager(requestId: string): void {
    this.loader.openLoading();
    this.slabApprovalRequestsService.fetchApprovalRequestById(requestId)
      .subscribe((row: any) => {
        if (!row || row.newValue == null) {
          this.loader.closeLoading();
          this.showError('Approval request not found');
          return;
        }
        this.applyApprovalRequestRow(requestId, row);
      }, () => {
        const previewData = this.readApprovalPreview(requestId);
        if (!previewData) {
          this.loader.closeLoading();
          this.showError('Approval preview data not found for request');
          return;
        }
        const context = this.approvalPreviewContext || { requestId, newValue: previewData };
        this.applyApprovalRequestRow(requestId, {
          ...context,
          newValue: previewData,
        });
      });
  }

  private applyApprovalRequestRow(requestId: string, row: any): void {
    const requestStatus = row ? String(row.requestStatus || row.status || '').trim() : '';
    this.approvalPreviewContext = {
      requestId,
      requestStatus,
      operation: row.operation,
      createdBy: row.createdBy,
      resourceId: row.resourceId,
      resource: row.resource,
      key: row.key,
      version: row.version,
      newValue: row.newValue,
      oldValue: row.oldValue || null,
    };
    this.approvalRequestStatus = requestStatus;
    const normalized = requestStatus.trim().toLowerCase();
    this.approvalRequestStatusDisplay = requestStatus
      ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
      : 'Unknown';
    this.approvalRequestCreatedBy = row.createdBy || '';
    this.isApproved = normalized === 'approved';
    this.approvalRequestApprovedBy = this.isApproved ? (row.statusChangedBy || '') : '';
    this.initApprovalPermissions();
    this.mapApiSlabToForm(row.newValue);
    this.loader.closeLoading();
  }

  private readApprovalPreview(requestId: string) {
    const raw = sessionStorage.getItem(`${SLAB_APPROVAL_PREVIEW_KEY_PREFIX}${requestId}`);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.newValue != null) {
        this.approvalPreviewContext = parsed as SlabApprovalPreviewContext;
        this.updateApprovalActionVisibility();
        return parsed.newValue;
      }
      this.approvalPreviewContext = { requestId, newValue: parsed };
      this.updateApprovalActionVisibility();
      return parsed;
    } catch (error) {
      return null;
    }
  }

  private async initDesiredCustomerPpkmPermission(): Promise<void> {
    try {
      this.canSetDesiredCustomerPpkm = await this.authService.isAuthorizedFor('slab_desired_customer_ppkm');
    } catch (error) {
      this.canSetDesiredCustomerPpkm = false;
    }
  }

  private initApprovalPermissions(): void {
    this.authService.getUserDetails().subscribe((user: any) => {
      const slabPermissions = permissionsForFeature(AUTHZ_FEATURES.SLAB, this.authService.getAuthzPanels());
      this.hasApprovePermission = this.authService.isAuthorizedFor(slabPermissions.approve, AUTHZ_FEATURES.SLAB);
      this.hasCreatePermission = this.authService.isAuthorizedFor(slabPermissions.create, AUTHZ_FEATURES.SLAB);
      this.currentUserEmail = (user && user.email) || '';
      this.updateApprovalActionVisibility();
    });
  }

  private updateApprovalActionVisibility(): void {
    const status = (this.approvalRequestStatus || '').toLowerCase();
    const isPending = !status || status === 'pending';
    const isSelfCreated = this.isSamePersonAsRequestCreator();

    // Approve: requires approve_slab permission AND not self-created
    this.canApprove = this.hasApprovePermission && !isSelfCreated && isPending;

    // Reject: requires approve_slab OR (self-created AND create_slab)
    this.canReject = isPending && (this.hasApprovePermission || (isSelfCreated && this.hasCreatePermission));

    // Show actions section if either action is available
    this.canShowApprovalActions = this.canApprove || this.canReject;

    // Set message when buttons are hidden
    this.approvalActionMessage = '';
    if (!this.canShowApprovalActions && status) {
      if (!this.hasApprovePermission && !(isSelfCreated && this.hasCreatePermission)) {
        this.approvalActionMessage = "You don't have permission to approve or reject slab requests";
      } else if (!isPending) {
        this.approvalActionMessage = "Approve and Reject are only available while the request is pending";
      }
    }
  }

  private isSamePersonAsRequestCreator(): boolean {
    const creator = this.approvalPreviewContext && this.approvalPreviewContext.createdBy;
    if (!creator || !this.currentUserEmail) {
      return false;
    }
    return (creator || '').trim().toLowerCase() === (this.currentUserEmail || '').trim().toLowerCase();
  }

  async approveSlabRequest(): Promise<void> {
    if (this.isSamePersonAsRequestCreator()) {
      this.alertDialogService.open({
        title: 'Cannot approve',
        message: 'This request cannot be approved by the same person who submitted it. Please ask another approver to review this request.',
      });
      return;
    }
    await this.submitApprovalRequest('approve');
  }

  async rejectSlabRequest(): Promise<void> {
    await this.submitApprovalRequest('reject');
  }

  private async submitApprovalRequest(action: 'approve' | 'reject'): Promise<void> {
    if (!this.approvalRequestId || this.processingApprovalRequest) {
      return;
    }
    this.processingApprovalRequest = true;
    this.loader.openLoading();
    try {
      if (!this.approvalPreviewContext) {
        throw { error: { message: 'Approval request data is missing. Re-open the request from the list.' } };
      }
      const resource = this.approvalPreviewContext.resource || 'slabs';
      const body = { resource, comment: action === 'approve' ? 'Approved' : 'Rejected' };
      const request$ = action === 'approve'
        ? this.slabApprovalRequestsService.approveRequest(this.approvalRequestId, body)
        : this.slabApprovalRequestsService.rejectRequest(this.approvalRequestId, body);
      await request$.toPromise();
      this.alertDialogService.open({
        title: 'Success',
        message: action === 'approve'
          ? 'Slab approval request was approved successfully.'
          : 'Slab approval request was rejected successfully.',
      });
      this.canShowApprovalActions = false;
      this.approvalRequestStatus = action === 'approve' ? 'approved' : 'rejected';
      this.approvalRequestStatusDisplay = action === 'approve' ? 'Approved' : 'Rejected';
    } catch (error) {
      const message = (error && error.error && error.error.message)
        || (error && error.error && error.error.data && error.error.data.error)
        || (action === 'approve'
          ? 'Failed to approve slab request.'
          : 'Failed to reject slab request.');
      this.alertDialogService.open({
        title: 'Failed',
        message,
      });
    } finally {
      this.processingApprovalRequest = false;
      this.loader.closeLoading();
    }
  }

  goBackFromSlabView(): void {
    if (this.isApprovalPreview) {
      this.router.navigate(['/slab-approval-requests']);
      return;
    }
    this.router.navigate(['/slabs']);
  }

  private fetchSlabAndMap(slabId: string): void {
    this.loader.openLoading();
    this.sharedService.getSlab(slabId).toPromise()
      .then((res: any) => {
        const slab = res && res.data != null ? res.data : res;
        if (!slab) {
          this.showError('Slab not found');
          return;
        }
        this.mapApiSlabToForm(slab);
      })
      .catch((err: any) => {
        const msg = (err && err.error && err.error.message) || (err && err.message) || 'Error loading slab';
        this.showError(msg);
      })
      .finally(() => this.loader.closeLoading());
  }

  getViewModeServiceDisplayText(): string {
    if (this.selectedServiceNames && this.selectedServiceNames.length > 0) {
      return this.selectedServiceNames.join(', ');
    }
    const ids = (this.params && this.params.serviceTypes) || [];
    return ids.map((id: string) => this.serviceNames[id] || id).join(', ');
  }

  /**
   * Maps GET slab API response into component form model (params and related state).
   * Extend when API response shape is known.
   */
  private mapApiSlabToForm(slab: any): void {
    if (slab && (slab as any).slab && typeof (slab as any).slab === 'object') {
      slab = (slab as any).slab;
    }
    const categoryKeys = ['category', 'type', 'categoryType', 'slabCategory', 'slab_category'];
    let rawCategory = null;
    for (const key of categoryKeys) {
      const val = key.indexOf('_') !== -1 ? (slab as any)[key] : slab[key];
      if (val != null && val !== '') {
        rawCategory = val;
        break;
      }
    }
    if (rawCategory == null && slab.data && typeof slab.data === 'object') {
      for (const key of categoryKeys) {
        const val = (slab.data as any)[key];
        if (val != null && val !== '') {
          rawCategory = val;
          break;
        }
      }
    }
    if (rawCategory == null && slab && typeof slab === 'object') {
      rawCategory = slab.peakSlab.slabData.category
    }
    if (rawCategory != null) {
      const normalized = typeof rawCategory === 'string' ? rawCategory.trim().toLowerCase() : String(rawCategory).toLowerCase();
      this.category = this.categories.indexOf(normalized) !== -1 ? normalized : rawCategory;
    }
    
    const modeId = slab.modeInfo && slab.modeInfo._id;
    const cityDisplayName = Array.isArray(slab.cities) && slab.cities.length > 0
      ? (typeof slab.cities[0] === 'string' ? slab.cities[0] : (slab.cities[0].displayName || slab.cities[0].name))
      : (slab.cities && typeof slab.cities === 'string' ? slab.cities : null);

    // Extract serviceIds from slab
    const serviceTypesFromSlab = slab.serviceTypes && Array.isArray(slab.serviceTypes) ? slab.serviceTypes : [];

    if (cityDisplayName) {
      this.selectedCities = cityDisplayName;
      const cityObj = this.cities.find((c: any) => c.displayName === cityDisplayName);
      if (cityObj) {

        // In view mode or duplicate mode, fetch modes using serviceIds
        if ((this.isViewMode || this.actionTitle === 'Duplicate') && serviceTypesFromSlab.length > 0) {
          const serviceNamesFromSlab = slab.serviceNames && Array.isArray(slab.serviceNames) ? slab.serviceNames : null;
          this.fetchModesByServiceDetailIdsAndValidate(serviceTypesFromSlab, modeId, cityDisplayName, serviceTypesFromSlab, serviceNamesFromSlab);
        } else {
          const presetModeId = modeId ? String(modeId) : null;
          this.fetchModesForCity(cityObj._id, presetModeId);
          const serviceNamesFromSlab = slab.serviceNames && Array.isArray(slab.serviceNames) ? slab.serviceNames : null;
          this.getCityServices(cityDisplayName, () => {
            this.params.serviceTypes = serviceTypesFromSlab;
            this.selectedServiceNames = serviceNamesFromSlab != null && serviceNamesFromSlab.length > 0
              ? serviceNamesFromSlab
              : (this.params.serviceTypes || []).map((id: string) => this.serviceNames[id] || id);
            this.servicesChanged();
          });
        }
      }
    }
    if (modeId && !this.isViewMode) {
      this.selectedMode = String(modeId);
    }
    if (slab.serviceTypes && Array.isArray(slab.serviceTypes)) {
      this.params.serviceTypes = slab.serviceTypes;
    } else if (slab.serviceNames && Array.isArray(slab.serviceNames)) {
      this.params.serviceTypes = slab.serviceTypes || [];
    }
    if (slab.shift != null) {
      this.params.shift = Array.isArray(slab.shift) ? slab.shift.slice() : [slab.shift];
      this.shifts = Array.isArray(slab.shift) ? slab.shift.slice() : [slab.shift];
    }
    if (slab.startDate != null) {
      this.params.startDate = typeof slab.startDate === 'string' ? slab.startDate.split('T')[0] : slab.startDate;
    }
    if (slab.endDate != null) {
      this.params.endDate = typeof slab.endDate === 'string' ? slab.endDate.split('T')[0] : slab.endDate;
    }
    if (slab.days != null) {
      this.params.days = Array.isArray(slab.days) ? slab.days.slice() : slab.days;
    }
    if (slab.slabMessage != null || (slab as any).slab_message != null) {
      this.params.slabMessage = slab.slabMessage != null ? slab.slabMessage : (slab as any).slab_message;
    }
    if (slab.batchedOrder != null) {
      this.isBatchedOrder = !!slab.batchedOrder;
    }
    if (slab.isFallbackSlab != null) {
      this.isFallbackSlab = !!slab.isFallbackSlab;
      this.params.isFallbackSlab = !!slab.isFallbackSlab;
    }
    // Pool config comes from captain-earnings as poolDetails { size, compensationMultipliers };
    // flatten it into the form fields
    const poolDetails = (slab as any).poolDetails;
    if (poolDetails != null) {
      this.params.poolSize = poolDetails.size;
      this.params.poolMultipliers = Utils.normalizePoolMultipliers(poolDetails.compensationMultipliers);
    }
    this.BatchedFirstOrderCategory = slab.BatchedFirstOrderCategory != null ? slab.BatchedFirstOrderCategory : (slab.batchedFirstOrderCategory != null ? slab.batchedFirstOrderCategory : null);
    this.BatchedNextOrderCategory = slab.BatchedNextOrderCategory != null ? slab.BatchedNextOrderCategory : (slab.batchedNextOrderCategory != null ? slab.batchedNextOrderCategory : null);
    const thresholdDistanceFM = slab.thresholdDistanceFM != null ? slab.thresholdDistanceFM : (slab as any).threshold_distance_fm;
    const thresholdFareFM = slab.thresholdFareFM != null ? slab.thresholdFareFM : (slab as any).threshold_fare_fm;
    this.thresholdDistanceFM = thresholdDistanceFM != null ? thresholdDistanceFM : null;
    this.thresholdFareFM = thresholdFareFM != null ? thresholdFareFM : null;
    this.maxCompensationFM = slab.maxCompensationFM != null ? slab.maxCompensationFM : null;
    this.minCompensationFM = slab.minCompensationFM != null ? slab.minCompensationFM : null;
    this.thresholdDistanceLM = slab.thresholdDistanceLM != null ? slab.thresholdDistanceLM : null;
    this.thresholdFareLM = slab.thresholdFareLM != null ? slab.thresholdFareLM : null;
    this.desiredCustomerPpkm = slab.desiredCustomerPpkm != null ? slab.desiredCustomerPpkm : null;
    if ((this.isViewMode || this.actionTitle === 'Duplicate') && modeId) {
      this.mode = String(modeId);
      if (this.isViewMode && !this.selectedMode) {
        this.selectedMode = String(modeId);
      }
      if (this.isViewMode && slab.modeInfo && slab.modeInfo.name) {
        this.selectedModeNames = [slab.modeInfo.name];
      }
    }
    const slabKeys = [
      'excludeIncentiveTypes', 'calculationType', 'isSalaried', 'payrollVersion',
      'commissionType', 'commissionAmount', 'commissionOnBonus',
      'commissionTypeBatchedFirstOrder', 'commissionAmountBatchedFirstOrder', 'commissionOnBonusBatchedFirstOrder',
      'commissionTypeBatchedNextOrder', 'commissionAmountBatchedNextOrder', 'commissionOnBonusBatchedNextOrder',
      'peakSlab', 'nonPeakSlab', 'flexibleSlab', 'nightSlab',
      'peakSlabBatchedFirstOrder', 'nonPeakSlabBatchedFirstOrder', 'flexibleSlabBatchedFirstOrder', 'nightSlabBatchedFirstOrder',
      'peakSlabBatchedNextOrder', 'nonPeakSlabBatchedNextOrder', 'flexibleSlabBatchedNextOrder', 'nightSlabBatchedNextOrder',
      'earningModel', 'minimumPayout', 'timeBasedSlabs', 'batchDetails', 'slabMessage',
      'isSurgeIncluded', 'isSurgeIncludedBatchedFirstOrder', 'isSurgeIncludedBatchedNextOrder',
      'minimumGuaranteeDetails'
    ];
    slabKeys.forEach(k => {
      if (slab[k] !== undefined && slab[k] !== null) {
        if (typeof slab[k] === 'object' && !Array.isArray(slab[k]) && this.params[k] && typeof this.params[k] === 'object') {
          this.params[k] = JSON.parse(JSON.stringify(slab[k]));
        } else {
          this.params[k] = slab[k];
        }
      }
    });
    const inclusiveCommission = slab.commissionWithAdditionalDeductions;
    this.slabHasCommissionWithAdditionalDeductions =
      inclusiveCommission != null && inclusiveCommission !== '';
    if (this.slabHasCommissionWithAdditionalDeductions) {
      this.params.commissionAmount = inclusiveCommission;
    }
    this.selectedServiceNames = (this.params.serviceTypes || []).map((id: string) => this.serviceNames[id] || id);
    if (slab.serviceNames && Array.isArray(slab.serviceNames)) {
      this.selectedServiceNames = slab.serviceNames;
    }
    if (!this.category && this.params.earningModel) {
      const em = String(this.params.earningModel).trim().toLowerCase();
      if (this.categories.indexOf(em) !== -1) {
        this.category = em;
      }
    }
    if (this.params.timeBasedSlabs && Array.isArray(this.params.timeBasedSlabs) && this.params.timeBasedSlabs.length > 0) {
      this.timeBasedSlabs = this.params.timeBasedSlabs.map((tb: any) => ({
        startTime: tb.startTime,
        endTime: tb.endTime,
        rules: Array.isArray(tb.rules) && tb.rules.length > 0
          ? tb.rules.map((r: any) => ({ type: r.type || 'percentage', value: r.value }))
          : [{ type: 'percentage', value: null }]
      }));
    }
    this.setEarningModels();
    this.evaluateFlatCommissionDeductionsState();
    this.blockDuplicateFlowIfSlabCategoryNotInConfig();
  }

  private blockDuplicateFlowIfSlabCategoryNotInConfig(): void {
    if (this.actionTitle !== 'Duplicate' || this.duplicateSlabConfigBlockHandled || !this.category || !this.dashboardConfig) {
      return;
    }

    const modeId = this.mode || this.selectedMode;
    if (!modeId) {
      return;
    }

    if (this.sharedService.isSlabCategoryConfiguredForMode(this.dashboardConfig, modeId, this.category)) {
      return;
    }
    this.duplicateSlabConfigBlockHandled = true;
    const categoryTitle = String(this.category);
    this.showError(
      `Duplicating "${categoryTitle}" category slab is not allowed.`
    );
    this.router.navigate(['/slabs']);
  }

  fetchNewShifts(cityDisplayName: string) {
    if (!cityDisplayName) { return; }
    this.sharedService.fetchShifts(cityDisplayName).toPromise()
      .then((res: any) => {
        const data: string[] = Array.isArray(res) ? res : (res && res.data ? res.data : []);
        // Store raw data without "All" - we'll add it during merge
        this.oldApiShifts = data;
        this.mergeAndSetShifts();
      })
      .catch(err => console.error('Fetch old shifts error', err));
  }

  private fetchModesForCity(cityId: string, presetModeId?: string) {
    if (!cityId) { return; }
    this.sharedService.fetchModes(cityId).toPromise()
      .then((res: any) => {
        this.modes = (res && res.data && res.data.modes) || [];
        this.selectedModeNames = [];
        if (presetModeId) {
          this.selectedMode = presetModeId;
          this.filterServicesByMode();
        }
        if (this.actionTitle === 'Duplicate' && this.selectedCities && this.selectedMode) {
          this.loadShiftsListForCurrentCityAndMode();
        }
      })
      .catch(err => console.error('Fetch modes error', err));
  }


  private fetchModesByServiceDetailIdsAndValidate(serviceDetailIds: string[], slabModeId: string | null, cityDisplayName: string, serviceTypesFromSlab: any[], serviceNamesFromSlab: string[] | null) {
    if (!serviceDetailIds || serviceDetailIds.length === 0) return;

    this.sharedService.fetchModesByServiceDetailIds(serviceDetailIds).toPromise()
      .then((res: any) => {
        const modes = (res && res.data && res.data.modes) || [];
        this.modes = modes;
        const uniqueModeIds = Array.from(
          new Set(modes.map((m: any) => m.id).filter(Boolean))
        );
        this.selectedModeNames = Array.from(
          new Set(modes.map((m: any) => m.name).filter(Boolean))
        );
        const slabMode = slabModeId ? String(slabModeId) : uniqueModeIds.length === 1 ? String(uniqueModeIds[0]) : null;

        const isSingleMode = uniqueModeIds.length === 1;
        const modeMatches = isSingleMode && slabMode === uniqueModeIds[0];

        this.selectedMode = isSingleMode ? String(uniqueModeIds[0]) : null;

        this.getCityServices(cityDisplayName, () => {
          this.filterServicesByMode();

          const isViewOrDuplicate = this.actionTitle === 'Duplicate' || this.isViewMode;

          if (isViewOrDuplicate) {
            const validServiceIds = serviceDetailIds.filter((id: string) =>
              this.allServices.some((s: any) => String(s._id) === String(id))
            );

            this.params.serviceTypes = validServiceIds;

            this.selectedServiceNames = serviceNamesFromSlab && serviceNamesFromSlab.length > 0
              ? serviceNamesFromSlab
              : validServiceIds.map((id: string) => this.serviceNames[id] || id);

            this.servicesChanged();
            if (this.actionTitle === 'Duplicate') {
              this.loadShiftsListForCurrentCityAndMode();
            }
            if (this.isViewMode && !modeMatches) {
              console.warn('Mode mismatch (view mode):', slabMode, uniqueModeIds);
            }
          } else {
            console.warn('Mode mismatch:', slabMode, uniqueModeIds);
            this.params.serviceTypes = [];
            this.selectedServiceNames = [];
          }
        });
      })
      .catch((err: any) => {
        console.error('Fetch modes error:', err);

        const city = this.cities.find((c: any) => c.displayName === cityDisplayName);
        if (city) this.fetchModesForCity(city._id);
      });
  }

  private loadShiftsListForCurrentCityAndMode(): void {
    if (!this.selectedCities || !this.selectedMode) { return; }
    const cityObj = this.cities.find((c: any) => c.displayName === this.selectedCities);
    if (!cityObj) { return; }
    if (!this.isFetchingShiftsFromSlabEnabled) {
      this.fetchNewShifts(this.selectedCities);
    }
    this.fetchCaptainShifts(cityObj._id, this.selectedMode, this.params.isFallbackSlab);
  }

  private fetchCaptainShifts(cityId: string, modeId: string, isDefault?: boolean) {
    if (!cityId || !modeId) { return; }
    this.defaultShiftsFetchedAndEmpty = false;
    this.sharedService.fetchCaptainEarningsShifts(cityId, modeId, isDefault).toPromise()
      .then((res: any) => {
        let arr: any[] = [];
        if (res && res.data && res.data.shifts && Array.isArray(res.data.shifts)) {
          arr = res.data.shifts;
        } else if (res && res.data && Array.isArray(res.data)) {
          arr = res.data;
        } else if (Array.isArray(res)) {
          arr = res;
        }
        this.newApiShifts = arr
          .map((s: any) => (typeof s === 'object' && s !== null) ? (s.name || '') : String(s))
          .filter((name: string) => name.trim() !== '');
        this.mergeAndSetShifts();
        this.defaultShiftsFetchedAndEmpty = !!(isDefault && this.shifts.length === 0);
        this.applyFallbackSingleShiftSelection();
      })
      .catch(err => console.error('Fetch captain shifts error', err));
  }

  private applyFallbackSingleShiftSelection(): void {
    if (!this.isFallbackSlab || this.isViewMode) { return; }
    if (this.shiftSelection !== 'existing') { return; }
    if (this.defaultShiftsFetchedAndEmpty) { return; }
    if (this.shifts.length !== 1) { return; }
    this.params.shift = [this.shifts[0]];
  }

  isFallbackShiftsReadonlyBox(): boolean {
    if (!this.isFallbackSlab || this.shiftSelection !== 'existing') { return false; }
    if (this.defaultShiftsFetchedAndEmpty) { return false; }
    if (this.shifts.length === 1) { return true; }
    return this.isViewMode && Array.isArray(this.params.shift) && this.params.shift.length === 1;
  }

  getFallbackShiftDisplayName(): string {
    if (this.shifts.length === 1) { return this.shifts[0]; }
    if (this.params.shift && this.params.shift.length > 0) { return this.params.shift[0]; }
    return '';
  }

  private mergeAndSetShifts() {
    if (this.isFetchingShiftsFromSlabEnabled) {
      // Toggle ON: new API only
      const unique = this.newApiShifts.filter((v, i, a) => a.indexOf(v) === i);
      this.shifts = unique;
    } else {
      const seen: { [key: string]: boolean } = {};
      const merged: string[] = [];

      this.oldApiShifts.forEach(s => {
        if (s && !seen[s]) {
          seen[s] = true;
          merged.push(s);
        }
      });

      this.newApiShifts.forEach(s => {
        if (s && !seen[s]) {
          seen[s] = true;
          merged.push(s);
        }
      });
      this.shifts = merged;
    }
  }

  onModeChange() {
    this.defaultShiftsFetchedAndEmpty = false;
    this.newApiShifts = [];
    this.mergeAndSetShifts();
    this.filterServicesByMode();
    this.params.shift = [];
    if (this.params.serviceTypes && this.params.serviceTypes.length > 0) {
      const validServices = this.services.filter(s =>
        this.params.serviceTypes.includes(s._id)
      );
      if (validServices.length === 0) {
        this.params.serviceTypes = [];
        this.servicesChanged();
      }
    }
    if (!this.selectedMode || !this.selectedCities) { return; }
    const cityObj = this.cities.find(c => c.displayName === this.selectedCities);
    if (!this.isGeoSlabPage && cityObj) {
      this.fetchCaptainShifts(cityObj._id, this.selectedMode, this.params.isFallbackSlab);
    }
    this.evaluateMinimumGuaranteeState(true);
    if (this.isGeoSlabPage) {
      this.selectedLayer = null;
      this.layers = [];
      this.refreshGeoSlabLayers();
      this.lookupExistingGeoSlab();
    }
  }

  onDefaultSlabToggle() {
    this.params.isFallbackSlab = !!this.isFallbackSlab;
    this.defaultShiftsFetchedAndEmpty = false;
    this.filterServicesByMode();

    if (!this.selectedMode || !this.selectedCities) { return; }
    const cityObj = this.cities.find(c => c.displayName === this.selectedCities);
    if (cityObj) {
      this.params.shift = [];
      this.shifts = [];
      this.newApiShifts = [];
      this.mergeAndSetShifts();
      this.fetchCaptainShifts(cityObj._id, this.selectedMode, this.params.isFallbackSlab);
    }
    this.evaluateMinimumGuaranteeState(true);
  }

  openStartDatePicker(): void {
    const ref = this.startDateInput;
    const el = ref ? ref.nativeElement : null;
    if (!el) return;
    const input = el as HTMLInputElement & { showPicker?: () => void };
    if (typeof input.showPicker === 'function') {
      input.showPicker();
    } else {
      el.focus();
      el.click();
    }
  }

  getCityServices(cityDisplayName: string, onLoaded?: () => void) {
    this.services = [];
    this.allServices = [];
    // Don't clear serviceTypes in view/duplicate — they will be set in the callback
    if (!this.isViewMode && this.actionTitle !== 'Duplicate') {
      this.params.serviceTypes = [];
    }
    if (!cityDisplayName) { return; }

    const cityObj = this.cities.find(c => c.displayName === cityDisplayName);
    if (!cityObj) { return; }
    const cityId = cityObj._id;

    this.sharedService.cityService(cityId).toPromise()
      .then((res: any) => {
        this.allServices = Array.isArray(res) ? res : (res && res.data ? res.data : []);
        this.serviceNames = {};
        this.serviceMap = {};

        this.allServices.forEach(s => {
          this.serviceNames[s._id] = s.service && s.service.name;
          const modeId = (s.modeInfo && (s.modeInfo._id || s.modeInfo.name)) || s.mode || null;
          this.serviceMap[s._id] = {
            mode: modeId,
            modeInfo: s.modeInfo,
            orderType: s.orderType,
            geoServiceGroupId: s.service && s.service.geoServiceGroupId
          };
        });

        this.filterServicesByMode();
        this.servicesChanged();
        if (onLoaded) {
          onLoaded();
        }
      })
      .catch(err => {
        console.error(err);
        this.showError('Error fetching services');
      });
  }

  private filterServicesByMode() {
    let filteredServices: any[];
    if (!this.selectedMode) {
      filteredServices = this.allServices.slice();
    } else {
      filteredServices = this.allServices.filter(s => {
        const serviceMapEntry = this.serviceMap[s._id];
        const serviceModeId = serviceMapEntry && serviceMapEntry.mode;
        return serviceModeId === this.selectedMode ||
               (serviceModeId && String(serviceModeId) === String(this.selectedMode));
      });
    }

    if (this.isFallbackSlab && environment.excludedServiceIdsForFallbackSlab) {
      const excludedServiceIds = environment.excludedServiceIdsForFallbackSlab;
      filteredServices = filteredServices.filter(service => {
        const serviceId = service.service._id;
        return !excludedServiceIds.includes(serviceId);
      });

      // Filter out excluded services from selected service types
      if (this.params.serviceTypes) {
        this.params.serviceTypes = this.params.serviceTypes.filter((serviceDetailId: string) => {
          const serviceDetail = this.allServices.find(s => String(s._id) === String(serviceDetailId));
          if (!serviceDetail) return true;
          const serviceId = serviceDetail.service && serviceDetail.service._id ? String(serviceDetail.service._id) : '';
          return !excludedServiceIds.includes(serviceId);
        });

        this.selectedServiceNames = (this.params.serviceTypes || []).map((id: string) => this.serviceNames[id] || id);
        this.servicesChanged();
      }
    }
    // Always assign a new array reference to trigger change detection
    // Sort: active services first, inactive at the bottom
    this.services = (filteredServices || []).sort((a, b) => {
      if (a.active === b.active && a.service.active === b.service.active) { return 0; }
      return a.active && a.service.active ? -1 : 1;
    });
  }

  private fetchDashboardConfig() {
    this.sharedService.fetchDashboardConfig().toPromise()
      .then((res: any) => {
        const payload = this.sharedService.parseDashboardConfigResponse(res);
        if (payload) {
          this.dashboardConfig = payload;
          this.minimumGuaranteeSlotCount = this.parseMinimumGuaranteeSlotCount(
            this.dashboardConfig.minimumGuaranteeSlotCount
          );
          this.syncMinimumGuaranteeSlotsInState();
          this.setEarningModels();
          this.blockDuplicateFlowIfSlabCategoryNotInConfig();
        } else {
          console.warn('[PayrollSlab] fetchDashboardConfig returned no data', res);
        }
      })
      .catch(err => console.error('[PayrollSlab] fetchDashboardConfig error:', err));
  }

  // ─────────────────────────────────────────────────────────────
  // City / service / shift handlers
  // ─────────────────────────────────────────────────────────────

  onCityChange(city: string) {
    this.clearFlatCommissionDeductions();
    this.defaultShiftsFetchedAndEmpty = false;
    this.shifts = [];
    this.params.shift = [];
    this.oldApiShifts = [];
    this.newApiShifts = [];
    this.selectedMode = null;
    this.selectedModeNames = [];
    this.modes = [];
    this.services = [];
    this.allServices = [];
    this.params.serviceTypes = [];
    this.selectedLayer = null;
    this.layers = [];
    this.layersError = false;
    this.clearExistingGeoSlabs();
    if (!this.isGeoSlabPage && !this.isFetchingShiftsFromSlabEnabled) {
      this.fetchNewShifts(city);
    }
    this.getCityServices(city);
    const cityObj = this.cities.find(c => c.displayName === city);
    if (cityObj) {
      this.fetchModesForCity(cityObj._id);
    }
    this.evaluateMinimumGuaranteeState(true);
    this.evaluateFlatCommissionDeductionsState();
    if (this.isGeoSlabPage) {
      this.refreshGeoSlabLayers();
    }
  }

  retryGeoSlabLayers() {
    this.refreshGeoSlabLayers();
  }

  onGeoLayerTypeChange() {
    this.selectedLayer = null;
    this.layers = [];
    this.layersError = false;
    this.clearExistingGeoSlabs();
    this.refreshGeoSlabLayers();
  }

  onGeoSlabLayerChange() {
    this.lookupExistingGeoSlab();
  }

  getGeoLayerTypeDisplayName(): string {
    const layerType = this.geoLayerTypes.find(t => t.id === this.selectedGeoLayerType);
    return layerType ? layerType.name : (this.selectedGeoLayerType || 'Layer');
  }

  getGeoSlabLayerDisplayName(): string {
    if (!this.selectedLayer) {
      return '';
    }
    const layer = this.layers.find(l => String(l.id) === String(this.selectedLayer));
    return layer ? layer.name : this.selectedLayer;
  }

  getExistingGeoSlabId(slab: any): string {
    if (!slab) {
      return '—';
    }
    const id = slab.id || slab._id;
    if (id && typeof id === 'object' && id.$oid) {
      return id.$oid;
    }
    return id != null ? String(id) : '—';
  }

  getExistingGeoSlabCity(slab: any): string {
    if (slab && Array.isArray(slab.cities) && slab.cities.length) {
      return slab.cities.join(', ');
    }
    return this.selectedCities || '—';
  }

  getExistingGeoSlabServiceNames(slab: any): string {
    if (slab && Array.isArray(slab.serviceNames) && slab.serviceNames.length) {
      return slab.serviceNames.join(', ');
    }
    const ids = (slab && (slab.serviceTypes || slab.serviceDetailIds)) || [];
    if (!ids.length) {
      return (this.selectedServiceNames || []).join(', ') || '—';
    }
    return ids.map((id: string) => this.serviceNames[id] || id).join(', ');
  }

  getExistingGeoSlabShifts(slab: any): string {
    const shift = slab && slab.shift;
    if (Array.isArray(shift) && shift.length) {
      return shift.join(', ');
    }
    return shift ? String(shift) : '—';
  }

  getExistingGeoSlabHotspot(slab: any): string {
    const layerId = slab && (slab.layerId != null ? slab.layerId : slab.geoLayerId);
    if (layerId == null || String(layerId) === '') {
      return '—';
    }
    if (String(layerId) === String(this.selectedLayer)) {
      return this.getGeoSlabLayerDisplayName();
    }
    return String(layerId);
  }

  getExistingGeoSlabRadiusValue(slab: any): number | string {
    const details = this.getGeoPickupFareConfig(slab);
    return details.radiusInMeters != null ? details.radiusInMeters : '—';
  }

  getExistingGeoSlabAmountValue(slab: any): number | string {
    const details = this.getGeoPickupFareConfig(slab);
    return details.amount != null ? details.amount : '—';
  }

  getExistingGeoSlabSections(slab: any): { label: string, rows: any[] }[] {
    const sections = [
      { key: 'peakSlab', label: 'Peak slab' },
      { key: 'nonPeakSlab', label: 'Non-peak slab' },
      { key: 'flexibleSlab', label: 'Flexible slab' },
      { key: 'nightSlab', label: 'Night slab' }
    ];
    return sections.map((section) => {
      const rows = (((slab && slab[section.key] && slab[section.key].slabData) || {}).slabs) || [];
      return {
        label: section.label,
        rows: Array.isArray(rows) ? rows.filter((row: any) => row && (row.fromValue != null || row.toValue != null || row.minPerAmount != null || row.bonus != null)) : []
      };
    }).filter((section) => section.rows.length > 0);
  }

  private getGeoServiceGroupIdsForCurrentSelection(): string[] {
    const sourceServices = (this.params.serviceTypes && this.params.serviceTypes.length > 0)
      ? this.allServices.filter(s => this.params.serviceTypes.indexOf(s._id) !== -1)
      : this.services;
    const groupIds: string[] = [];
    const seen: { [id: string]: boolean } = {};
    (sourceServices || []).forEach((s: any) => {
      const fromMap = this.serviceMap[s._id] && this.serviceMap[s._id].geoServiceGroupId;
      const groupId = fromMap || (s.service && s.service.geoServiceGroupId);
      if (groupId && !seen[groupId]) {
        seen[groupId] = true;
        groupIds.push(groupId);
      }
    });
    return groupIds;
  }

  private refreshGeoSlabLayers() {
    const cityObj = this.cities.find(c => c.displayName === this.selectedCities);
    if (!cityObj) {
      this.clearGeoSlabLayers();
      return;
    }
    if (this.selectedGeoLayerType !== 'hotspot' && !this.selectedMode) {
      this.clearGeoSlabLayers();
      return;
    }
    this.fetchGeoSlabLayers(cityObj._id, this.getGeoServiceGroupIdsForCurrentSelection());
  }

  private clearGeoSlabLayers() {
    this.geoLayersRequestId++;
    this.layers = [];
    this.layersLoading = false;
    this.layersError = false;
  }

  private fetchGeoSlabLayers(cityId: string, groupIds: string[] = []) {
    if (!cityId || !this.selectedGeoLayerType) {
      this.clearGeoSlabLayers();
      return;
    }
    const needsGroupId = this.selectedGeoLayerType === 'zone';
    if (needsGroupId && (!groupIds || groupIds.length === 0)) {
      this.clearGeoSlabLayers();
      return;
    }
    this.layersLoading = true;
    this.layersError = false;
    const requestId = ++this.geoLayersRequestId;
    const categories = this.selectedGeoLayerType === 'hotspot' ? ['Hail'] : [];
    this.sharedService.fetchLayers(cityId, this.selectedGeoLayerType, groupIds, categories).toPromise()
      .then((res: any) => {
        if (requestId !== this.geoLayersRequestId) {
          return;
        }
        const layers = (res && res.layers) || [];
        this.layers = layers.filter((layer: any) => layer && layer.active !== false);
      })
      .catch(() => {
        if (requestId !== this.geoLayersRequestId) {
          return;
        }
        this.layers = [];
        this.layersError = true;
        this.showError(this.layersErrorMessage);
      })
      .finally(() => {
        if (requestId !== this.geoLayersRequestId) {
          return;
        }
        this.layersLoading = false;
      });
  }

  private lookupExistingGeoSlab() {
    if (!this.isGeoSlabPage || !this.selectedCities || !this.selectedLayer || !this.selectedGeoLayerType) {
      this.existingGeoSlabsLoading = false;
      this.existingGeoSlabLookupCount = 0;
      this.clearExistingGeoSlabs();
      return Promise.resolve();
    }
    const cityObj = this.cities.find((c: any) => c.displayName === this.selectedCities);
    const cityId = cityObj && cityObj._id;
    if (!cityId) {
      this.existingGeoSlabsLoading = false;
      this.existingGeoSlabLookupCount = 0;
      this.clearExistingGeoSlabs();
      return Promise.resolve();
    }
    const requestId = ++this.existingGeoSlabLookupId;
    this.existingGeoSlabsLoading = true;
    return this.sharedService.fetchGeoSlabConfig(cityId, this.selectedLayer, this.selectedGeoLayerType).toPromise()
      .then((res: any) => {
        if (requestId !== this.existingGeoSlabLookupId) {
          return;
        }
        const slabId = res && res.data && res.data.slabId;
        if (!slabId) {
          this.existingGeoSlabLookupCount = 0;
          this.clearExistingGeoSlabs();
          return;
        }
        return this.sharedService.getSlab(slabId).toPromise().then((slabRes: any) => {
          if (requestId !== this.existingGeoSlabLookupId) {
            return;
          }
          const slab = this.unwrapSlabResponse(slabRes);
          this.setExistingGeoSlabs(this.geoFenceSlabMatches(slab) ? [slab] : []);
          this.existingGeoSlabLookupCount = this.existingGeoSlabs.length;
          if (this.existingGeoSlab) {
            const details = this.getGeoPickupFareConfig(this.existingGeoSlab);
            if (this.geoFenceRadius == null && details.radiusInMeters != null) {
              this.geoFenceRadius = Number(details.radiusInMeters);
            }
            if (this.geoFenceAmount == null && details.amount != null) {
              this.geoFenceAmount = Number(details.amount);
            }
          }
        });
      })
      .catch((err: any) => {
        if (requestId !== this.existingGeoSlabLookupId) {
          return;
        }
        this.clearExistingGeoSlabs();
        this.showError('Could not load existing Geo Slabs');
        throw err;
      })
      .then(() => {
        if (requestId !== this.existingGeoSlabLookupId) {
          return;
        }
        this.existingGeoSlabsLoading = false;
      }, (err: any) => {
        if (requestId !== this.existingGeoSlabLookupId) {
          return Promise.resolve();
        }
        this.existingGeoSlabsLoading = false;
        return Promise.reject(err);
      });
  }

  private clearExistingGeoSlabs() {
    this.existingGeoSlabs = [];
    this.existingGeoSlab = null;
  }

  private setExistingGeoSlabs(slabs: any[]) {
    this.existingGeoSlabs = slabs || [];
    this.existingGeoSlab = this.existingGeoSlabs[0] || null;
  }

  private unwrapSlabResponse(res: any): any {
    let slab = res && res.data != null ? res.data : res;
    if (slab && slab.slab && typeof slab.slab === 'object') {
      slab = slab.slab;
    }
    return slab || null;
  }

  private geoFenceSlabMatches(slab: any): boolean {
    if (!slab) {
      return false;
    }
    const layerId = slab.layerId != null ? slab.layerId : slab.geoLayerId;
    if (layerId == null || String(layerId) === '') {
      return true;
    }
    return String(layerId) === String(this.selectedLayer);
  }

  private getGeoPickupFareConfig(slab: any) {
    const config = (slab && (slab.geoFencePickupFareConfig || slab.geoFenceDetails)) || {};
    return {
      radiusInMeters: config.radiusInMeters != null ? config.radiusInMeters : config.radius,
      amount: config.amount
    };
  }

  onGeoSlabRadiusKeydown(event: KeyboardEvent): void {
    const key = event.key;
    if (key && key.length === 1 && key >= '0' && key <= '9') {
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const navigationKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (navigationKeys.indexOf(key) !== -1) {
      return;
    }
    event.preventDefault();
  }

  onGeoSlabRadiusInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = (input.value || '').replace(/\D/g, '');
    if (input.value !== digits) {
      input.value = digits;
    }
    this.geoFenceRadius = digits === '' ? null : Number(digits);
  }

  private applyGeoSlabPayloadFields(slab: any, userEmail: string) {
    const cityObj = this.cities.find((c: any) => c.displayName === this.selectedCities);
    slab.isGeoSlab = true;
    slab.layer = this.selectedGeoLayerType;
    slab.layerId = this.selectedLayer;
    slab.cityId = cityObj && cityObj._id ? cityObj._id : slab.cityId;
    slab.geoFencePickupFareConfig = {
      radiusInMeters: Number(this.geoFenceRadius),
      amount: Number(this.geoFenceAmount)
    };
    slab.createdBy = userEmail || slab.createdBy;
    slab.shift = [];
    delete slab.geoFenceDetails;
    delete slab.radius;
    delete slab.amount;
  }

  servicesChanged() {
    const selected: string[] = this.params.serviceTypes || [];
    if (selected.length === 0) {
      this.mode = null;
      this.setEarningModels();
      this.evaluateMinimumGuaranteeState(true);
      this.evaluateFlatCommissionDeductionsState();
      if (this.isGeoSlabPage) {
        this.selectedLayer = null;
        this.layers = [];
        this.clearExistingGeoSlabs();
        this.refreshGeoSlabLayers();
      }
      return;
    }
    const modes = new Set<string>(selected.map(id => this.serviceMap[id] && this.serviceMap[id].mode).filter(Boolean));

    if (this.isViewMode) {
      const selectedModeFromServices = modes.size === 1 ? (modes.values().next().value || null) : null;
      if (!this.mode && selectedModeFromServices) {
        this.mode = selectedModeFromServices;
      }
      this.setEarningModels();
      this.evaluateMinimumGuaranteeState(true);
      this.evaluateFlatCommissionDeductionsState();
      return;
    }
    if (modes.size > 1) {
      this.params.serviceTypes = [];
      this.mode = null;
      this.showError('Select services with same mode');
      this.evaluateMinimumGuaranteeState(true);
      this.evaluateFlatCommissionDeductionsState();
      return;
    }
    this.mode = modes.values().next().value || null;
    this.setEarningModels();
    this.applyFallbackSingleShiftSelection();
    this.evaluateMinimumGuaranteeState(true);
    this.blockDuplicateFlowIfSlabCategoryNotInConfig();
    this.evaluateFlatCommissionDeductionsState();
    if (this.isGeoSlabPage) {
      this.selectedLayer = null;
      this.layers = [];
      this.clearExistingGeoSlabs();
      this.refreshGeoSlabLayers();
    }
  }

  setEarningModels() {
    if (this.actionTitle !== 'View' && this.actionTitle !== 'Duplicate') {
      delete this.params.earningModel;
    }
    const cfg = this.dashboardConfig && this.dashboardConfig.earningModelsForModeAndSlabCategory;
    if (this.mode && this.category && cfg && cfg[this.mode]) {
      this.earningModels = cfg[this.mode][this.category] || [];
    } else {
      this.earningModels = [];
    }
    if ((this.isViewMode || this.actionTitle === 'Duplicate') && this.params.earningModel && this.earningModels.indexOf(this.params.earningModel) === -1) {
      this.earningModels = this.earningModels.slice();
      this.earningModels.push(this.params.earningModel);
    }
    if (cfg && cfg[this.mode]) {
      this.categories = Object.keys(cfg[this.mode]);
    }
    this.evaluateMinimumGuaranteeState();
  }

  earningModelChanged() {
    if (this.params.earningModel && this.params.earningModel === 'access_fee') {
      this.setCommissionAmountToZero();
      this.clearExtraKmFareData();
      this.clearPickUpExtraKmFareData();
    } else {
      this.reconcileCommissionFields();
    }
    this.evaluateMinimumGuaranteeState(true);
    this.warnIfMinimumGuaranteeWithMultipleShifts();
    this.evaluateFlatCommissionDeductionsState();
  }

  onCommissionTypeChange() {
    this.evaluateFlatCommissionDeductionsState();
  }

  shouldShowFlatCommissionDeductions(): boolean {
    if (this.category === 'order') {
      return false;
    }

    const earningModel = this.params && this.params.earningModel;
    if (!earningModel) {
      return false;
    }

    const isFlatCommission =
      earningModel === 'proximity_flat_commission' ||
      (earningModel === 'cm' && this.params.commissionType === 'rupees');

    if (!isFlatCommission) {
      return false;
    }

    if (this.isViewMode) {
      return this.slabHasCommissionWithAdditionalDeductions;
    }

    return true;
  }

  private shouldFetchFlatCommissionDeductions(): boolean {
    if (this.isViewMode || !this.shouldShowFlatCommissionDeductions()) {
      return false;
    }

    const cityObj = this.cities.find((c: any) => c.displayName === this.selectedCities);
    const serviceDetailIds = Array.isArray(this.params.serviceTypes)
      ? this.params.serviceTypes.filter((id: string) => !!id)
      : [];

    return !!(cityObj && cityObj._id && serviceDetailIds.length > 0);
  }

  evaluateFlatCommissionDeductionsState() {
    if (!this.shouldFetchFlatCommissionDeductions()) {
      this.clearFlatCommissionDeductions();
      return;
    }

    this.fetchFlatCommissionDeductions();
  }

  clearFlatCommissionDeductions() {
    this.flatCommissionDeductionsRequestId++;
    this.flatCommissionDeductionsLoading = false;
    this.flatCommissionDeductionsError = false;
    this.flatCommissionTax = null;
    this.flatCommissionPlatformFee = null;
    this.flatCommissionDeductionDetails = [];
    delete this.params.flatCommissionDeductions;
  }

  fetchFlatCommissionDeductions() {
    const cityObj = this.cities.find((c: any) => c.displayName === this.selectedCities);
    const serviceDetailIds = Array.isArray(this.params.serviceTypes)
      ? this.params.serviceTypes.filter((id: string) => !!id)
      : [];

    if (!cityObj || !cityObj._id || serviceDetailIds.length === 0) {
      this.clearFlatCommissionDeductions();
      return;
    }

    const requestId = ++this.flatCommissionDeductionsRequestId;
    this.flatCommissionDeductionsLoading = true;
    this.flatCommissionDeductionsError = false;

    this.sharedService.fetchFlatCommissionDeductions({
      cityId: cityObj._id,
      serviceDetailIds,
    }).toPromise()
      .then((res: any) => {
        if (requestId !== this.flatCommissionDeductionsRequestId) {
          return;
        }
        const details = res && Array.isArray(res.details) ? res.details : [];
        this.flatCommissionDeductionDetails = details;
        this.flatCommissionTax = details.length > 0
          ? details.reduce((max: number, item: any) => Math.max(max, Number(item.tax) || 0), 0)
          : null;
        this.flatCommissionPlatformFee = details.length > 0
          ? details.reduce((max: number, item: any) => Math.max(max, Number(item.platformFee) || 0), 0)
          : null;
        if (!this.isViewMode) {
          this.params.flatCommissionDeductions = {
            tax: this.flatCommissionTax,
            platformFee: this.flatCommissionPlatformFee,
            details,
          };
        }
      })
      .catch(() => {
        if (requestId !== this.flatCommissionDeductionsRequestId) {
          return;
        }
        this.flatCommissionDeductionsError = true;
        this.flatCommissionTax = null;
        this.flatCommissionPlatformFee = null;
        this.flatCommissionDeductionDetails = [];
        delete this.params.flatCommissionDeductions;
      })
      .finally(() => {
        if (requestId !== this.flatCommissionDeductionsRequestId) {
          return;
        }
        this.flatCommissionDeductionsLoading = false;
      });
  }

  get flatCommissionTaxDisplay(): string {
    return this.flatCommissionTax != null ? String(this.flatCommissionTax) : '';
  }

  get flatCommissionPlatformFeeDisplay(): string {
    return this.flatCommissionPlatformFee != null ? String(this.flatCommissionPlatformFee) : '';
  }

  get isSubmitDisabled(): boolean {
    return this.loadingIndicator || !this.hasValidFlatCommissionDeductions();
  }

  private hasValidFlatCommissionDeductions(): boolean {
    if (!this.shouldShowFlatCommissionDeductions()) {
      return true;
    }

    if (this.flatCommissionDeductionsLoading || this.flatCommissionDeductionsError) {
      return false;
    }

    return this.flatCommissionTax != null && this.flatCommissionPlatformFee != null;
  }

  private validateFlatCommissionDeductions(): void {
    if (!this.shouldShowFlatCommissionDeductions()) {
      return;
    }

    if (this.flatCommissionDeductionsLoading) {
      this.toastError('Tax and platform fee details are still loading. Please wait.');
    }

    if (this.flatCommissionDeductionsError) {
      this.toastError(this.fetchTaxesAndOrPlatformFeeErrorMessage);
    }

    if (this.flatCommissionTax == null || this.flatCommissionPlatformFee == null) {
      this.toastError('Tax and platform fee details are required.');
    }
  }

  onShiftChange() {
    this.evaluateMinimumGuaranteeState(true);
    this.warnIfMinimumGuaranteeWithMultipleShifts();
  }

  validateEarningModel() {
    if (this.earningModels.length > 0 && !this.params.earningModel) {
      this.toastError('Please select Earning Model');
    }
  }


  AreWeekDayAndEndDateRemoved(city: string, createdOn: any, screenAction: string): boolean {
    if (screenAction && screenAction === 'Duplicate') {
      createdOn = new Date().getTime();
    }
    if (!createdOn) {
      createdOn = new Date().getTime();
    }
    if (!this.dashboardConfig || !this.dashboardConfig.cities_where_weekdaysAndEndDateAreRemoved) {
      return false;
    }
    const cityObj = this.cities.find(c => c.displayName === city);
    const cityId = cityObj ? cityObj._id : '';
    const cfg = this.dashboardConfig.cities_where_weekdaysAndEndDateAreRemoved;
    return (cfg.hasOwnProperty(cityId) && cfg[cityId] < createdOn) ||
           (cfg.hasOwnProperty('others') && cfg['others'] < createdOn);
  }

  showRupeesCommission(): boolean {
    const model = this.params && this.params.earningModel;
    const types = model && this.dashboardConfig && this.dashboardConfig.commissionTypesForEarningModels
      ? this.dashboardConfig.commissionTypesForEarningModels[model]
      : null;
    return Array.isArray(types) && types.indexOf('rupees') !== -1;
  }

  showPercentCommission(): boolean {
    const model = this.params && this.params.earningModel;
    const types = model && this.dashboardConfig && this.dashboardConfig.commissionTypesForEarningModels
      ? this.dashboardConfig.commissionTypesForEarningModels[model]
      : null;
    return Array.isArray(types) && types.indexOf('percent') !== -1;
  }

  private reconcileCommissionFields(): void {
    if (this.params && this.params.earningModel === 'access_fee') {
      return;
    }
    const allowPercent = this.showPercentCommission();
    const allowRupees = this.showRupeesCommission();
    const allKeys = [
      'commissionType', 'commissionAmount', 'commissionOnBonus',
      'commissionTypeBatchedFirstOrder', 'commissionAmountBatchedFirstOrder', 'commissionOnBonusBatchedFirstOrder',
      'commissionTypeBatchedNextOrder', 'commissionAmountBatchedNextOrder', 'commissionOnBonusBatchedNextOrder',
    ];
    if (!allowPercent && !allowRupees) {
      allKeys.forEach(k => delete this.params[k]);
      return;
    }
    const groups: [string, string, string][] = [
      ['commissionType', 'commissionAmount', 'commissionOnBonus'],
      ['commissionTypeBatchedFirstOrder', 'commissionAmountBatchedFirstOrder', 'commissionOnBonusBatchedFirstOrder'],
      ['commissionTypeBatchedNextOrder', 'commissionAmountBatchedNextOrder', 'commissionOnBonusBatchedNextOrder'],
    ];
    for (const [typeKey, amountKey, bonusKey] of groups) {
      const type = this.params[typeKey];
      if (type === 'percent' && !allowPercent) {
        delete this.params[typeKey];
        delete this.params[amountKey];
        delete this.params[bonusKey];
      } else if (type === 'rupees' && !allowRupees) {
        delete this.params[typeKey];
        delete this.params[amountKey];
        delete this.params[bonusKey];
      }
    }
  }

  private clearBatchedSlabData() {
    const keys = [
      'peakSlabBatchedFirstOrder', 'peakSlabBatchedNextOrder',
      'nonPeakSlabBatchedFirstOrder', 'nonPeakSlabBatchedNextOrder',
      'flexibleSlabBatchedFirstOrder', 'flexibleSlabBatchedNextOrder',
      'nightSlabBatchedFirstOrder', 'nightSlabBatchedNextOrder',
      'commissionAmountBatchedFirstOrder', 'commissionAmountBatchedNextOrder',
      'commissionTypeBatchedFirstOrder', 'commissionTypeBatchedNextOrder',
      'commissionOnBonusBatchedFirstOrder', 'commissionOnBonusBatchedNextOrder',
      'isSurgeIncludedBatchedFirstOrder', 'isSurgeIncludedBatchedNextOrder'
    ];
    keys.forEach(k => delete this.params[k]);
  }

  private clearPickUpExtraKmFareData() {
    this.thresholdDistanceFM = null;
    this.thresholdFareFM = null;
    this.minCompensationFM = null;
    this.maxCompensationFM = null;
  }

  private clearExtraKmFareData() {
    this.thresholdDistanceLM = null;
    this.thresholdFareLM = null;
  }

  private clearTimeBasedSlabData() {
    this.timeBasedSlabs = [];
    delete this.params.timeBasedSlabs;
  }

  private clearMinimumPayoutData() {
    delete this.params.minimumPayout;
  }

  private setSubSlabstoEmpty() {
    this.params.peakSlab     = { timeData: [{}], slabData: { slabs: [{}] } };
    this.params.nonPeakSlab  = { timeData: [{}], slabData: { slabs: [{}] } };
    this.params.flexibleSlab = { timeData: [{}], slabData: { slabs: [{}] } };
    this.params.nightSlab    = { timeData: [{}], slabData: { slabs: [{}] } };
  }

  private setCommissionAmountToZero() {
    this.params.commissionType   = 'percent';
    this.params.commissionAmount = 0;
  }

  clearSlabData(type: string) {
    switch (type) {
      case 'distance':
        this.clearBatchedSlabData();
        this.setCommissionAmountToZero();
        // Only peak slab for distance; hardcode to full day
        this.params.peakSlab = { timeData: [{ fromTime: '00:00', toTime: '23:59' }], slabData: { slabs: [{}] } };
        delete this.params.nonPeakSlab;
        delete this.params.flexibleSlab;
        delete this.params.nightSlab;
        break;
      case 'cm':
        this.clearBatchedSlabData();
        this.clearExtraKmFareData();
        this.clearTimeBasedSlabData();
        this.clearMinimumPayoutData();
        this.setSubSlabstoEmpty();
        break;
      case 'order':
        this.clearTimeBasedSlabData();
        this.clearMinimumPayoutData();
        break;
      case 'minimum_guarantee':
        this.clearBatchedSlabData();
        this.clearTimeBasedSlabData();
        this.clearMinimumPayoutData();
        this.setSubSlabstoEmpty();
        break;
    }
    this.setEarningModels();
  }

  dismissMinimumGuaranteeBanner() {
    if (this.currentMinimumGuaranteeContextKey) {
      this.minimumGuaranteeBannerDismissedByContext[this.currentMinimumGuaranteeContextKey] = true;
    }
    this.showMinimumGuaranteeBanner = false;
  }

  addMinimumGuaranteeSlot() {
    if (this.isViewMode || this.isMinimumGuaranteeReadOnly || this.isMinimumGuaranteeSlotTimeLocked()) { return; }
    this.ensureMinimumGuaranteeDetailsObject(false);
    const maxSlots = this.minimumGuaranteeSlotCount;
    const slots = this.params.minimumGuaranteeDetails.slots || [];
    if (slots.length >= maxSlots) {
      this.showError(
        `You can add at most ${maxSlots} Minimum Guarantee time slot${maxSlots === 1 ? '' : 's'} ` +
        `(configured limit). Remove a slot or adjust configuration if you need more.`
      );
      return;
    }
    this.params.minimumGuaranteeDetails.slots.push({ startTime: '', endTime: '' });
  }

  removeMinimumGuaranteeSlot(index: number) {
    if (this.isViewMode || this.isMinimumGuaranteeReadOnly || this.isMinimumGuaranteeSlotTimeLocked()) { return; }
    this.ensureMinimumGuaranteeDetailsObject(false);
    const slots = this.params.minimumGuaranteeDetails.slots;
    if (!Array.isArray(slots) || slots.length === 0) { return; }
    if (slots.length <= 1) {
      this.showError('At least one Minimum Guarantee time slot is required.');
      return;
    }
    slots.splice(index, 1);
  }


  addSlabTime(slabType: string) {
    if (!this.params[slabType]) {
      this.params[slabType] = { timeData: [{}], slabData: { slabs: [{}] } };
    }
    if (!this.params[slabType].timeData) {
      this.params[slabType].timeData = [];
    }
    this.params[slabType].timeData.push({});
  }

  addSlabData(slabType: string) {
    if (!this.params[slabType]) {
      this.params[slabType] = { timeData: [{}], slabData: { slabs: [{}] } };
    }
    if (!this.params[slabType].slabData) {
      this.params[slabType].slabData = { slabs: [] };
    }
    if (!this.params[slabType].slabData.slabs) {
      this.params[slabType].slabData.slabs = [];
    }
    for (const item of this.params[slabType].slabData.slabs) {
      if (this.isEmpty(item.toValue) || this.isEmpty(item.fromValue) || this.isEmpty(item.minPerAmount)) {
        this.showError('Please enter complete data first');
        return;
      }
    }
    const newSlabIndex = this.params[slabType].slabData.slabs.length;
    this.params[slabType].slabData.slabs.push({ slabTitle: newSlabIndex });
    if (this.category === 'order' && this.params[slabType].slabData.slabs.length > 0) {
      const slabs = this.params[slabType].slabData.slabs;
      slabs[slabs.length - 1].minPerAmount = slabs[0].minPerAmount;
    }
  }

  removeDataFromSlab(slabType: string, dataType: string, index: number) {
    if (dataType === 'time') {
      this.params[slabType].timeData = this.params[slabType].timeData.filter((_: any, i: number) => i !== index);
    } else if (dataType === 'slab' && this.params[slabType].slabData.slabs.length > 0) {
      this.params[slabType].slabData.slabs.pop();
    } else {
      this.showError(`No ${slabType} slabs to remove`);
    }
  }

  updateMinPerAmount(slab: string, value: number) {
    if (slab === 'peak') {
      this.params.peakSlab.slabData.slabs.forEach((item: any) => {
        item.minPerAmount = value;
      });
    }
  }

  updateMinPerAmountBatched(slabType: string, value: number) {
    if (slabType === 'peak') {
      this.params.peakSlabBatchedFirstOrder.slabData.slabs.forEach((item: any) => {
        item.minPerAmount = value;
      });
    }
  }

  updateMinPerAmount2(slabName: string, _slab: string, value: number) {
    // propagate per-order value to all slabs in a batched section
    if (this.params[slabName] && this.params[slabName].slabData) {
      this.params[slabName].slabData.slabs.forEach((item: any) => {
        item.minPerAmount = value;
      });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Time-based slab management (distance category)
  // ─────────────────────────────────────────────────────────────

  addTimeBasedSlab() {
    if (this.timeBasedSlabs.length >= 1) {
      this.toastError('Only one timeBasedSlab can be configured');
      return;
    }
    this.timeBasedSlabs.push({ startTime: null, endTime: null, rules: [{ type: 'percentage', value: null }] });
  }

  removeTimeBasedSlab(index: number) {
    this.timeBasedSlabs.splice(index, 1);
  }

  // Validation helpers

  private isEmpty(value: any): boolean {
    return value == null || value === '' || (Array.isArray(value) && value.length === 0);
  }

  private checkDuplicateInObject(propertyName: string, inputArray: any[]): boolean {
    const testObject: any = {};
    let seenDuplicate = false;
    inputArray.forEach(item => {
      const key = item[propertyName];
      if (key in testObject) {
        testObject[key].duplicate = true;
        item.duplicate = true;
        seenDuplicate = true;
      } else {
        testObject[key] = item;
        delete item.duplicate;
      }
    });
    return seenDuplicate;
  }

  private fixTimes(slabArray: any) {
    if (slabArray && slabArray.timeData && slabArray.timeData.length > 0) {
      slabArray.timeData.forEach((t: any) => {
        // Ensure times are in 24-hour format (HH:mm)
        t.fromTime = this.ensure24HourFormat(t.fromTime) || 'null';
        t.toTime   = this.ensure24HourFormat(t.toTime) || 'null';
      });
    }
  }

  convertTo24Hour(time: string): string {
    if (!time || time === 'null') {
      return time;
    }

    const trimmed = time.trim().toUpperCase();

    // Check if it contains AM/PM (shouldn't happen with HTML5 time inputs, but handle it)
    if (trimmed.includes('AM') || trimmed.includes('PM')) {
      const hasAM = trimmed.includes('AM');
      const hasPM = trimmed.includes('PM');
      const timePart = trimmed.replace(/\s*(AM|PM)\s*/i, '');
      const parts = timePart.split(':');

      if (parts.length >= 2) {
        let hours = parseInt(parts[0], 10);
        const minutes = parts[1].replace(/\D/g, '').padStart(2, '0');

        if (hasPM) {
          if (hours !== 12) {
            hours += 12;
          }
        } else if (hasAM) {
          if (hours === 12) {
            hours = 0;
          }
        }

        const result = `${String(hours).padStart(2, '0')}:${minutes}`;
        return result;
      }
    }

    // HTML5 time inputs should return 24-hour format automatically
    // However, if the browser is somehow returning 12-hour format values,
    // we need to detect and convert them
    // Strip seconds if present and ensure proper formatting
    const parts = trimmed.split(':');
    if (parts.length >= 2) {
      let hours = parseInt(parts[0].replace(/\D/g, ''), 10);
      const minutes = parts[1].replace(/\D/g, '').padStart(2, '0');

      // If hours is 1-12 and the original string might have been PM,
      // we can't tell without AM/PM indicator, so trust the browser's value
      // HTML5 time inputs should always return correct 24-hour format
      const result = `${String(hours).padStart(2, '0')}:${minutes}`;
      return result;
    }

    return trimmed.length > 5 ? trimmed.substring(0, 5) : trimmed;
  }

  private ensure24HourFormat(time: string): string {
    return this.convertTo24Hour(time);
  }

  private timeDataValidation(slabArray: any) {
    if (slabArray && slabArray.timeData && slabArray.timeData.length !== 0) {
      slabArray.timeData = slabArray.timeData.filter((slab: any) => {
        const keys = Object.keys(slab);
        return keys.includes('fromTime') && keys.includes('toTime');
      });
    }
  }

  private removeEmptyTimeData(slabArray: any) {
    if (slabArray && slabArray.timeData) {
      slabArray.timeData = slabArray.timeData.filter((slab: any) => Object.keys(slab).length > 0);
    }
  }

  private slabDataValidation(slabArray: any) {
    if (slabArray && slabArray.slabData && slabArray.slabData.slabs && slabArray.slabData.slabs.length !== 0) {
      slabArray.slabData.category = this.category;
      slabArray.slabData.slabs = slabArray.slabData.slabs.reduce((acc: any[], slab: any) => {

        const hasAnyData = !this.isEmpty(slab.fromValue) || !this.isEmpty(slab.toValue) ||
                          !this.isEmpty(slab.minPerAmount) || !this.isEmpty(slab.slabTitle) ||
                          !this.isEmpty(slab.bonus);

        // If slab has any data, keep it for validation (validateForEmptyValues will catch missing fields)
        // Only filter out if it's completely empty
        if (hasAnyData) {
          if (!slab.slabType) {
            slab.slabType = 'perUnitPay';
          }
          if (slab.bonus === undefined || slab.bonus === null) {
            slab.bonus = 0;
          }
          acc.push(slab);
        }
        return acc;
      }, []);
    }
  }

  private validateForEmptyValues(slabArray: any, type: string) {
    if (slabArray && slabArray.slabData && slabArray.slabData.slabs) {
      slabArray.timeData.forEach((data: any) => {
        if (data.fromTime > data.toTime) {
          this.toastError(`From time is greater than to time in a ${type} slab`);
        }
      });
      slabArray.slabData.slabs.forEach((item: any) => {
        if (this.isEmpty(item.toValue) || this.isEmpty(item.fromValue) || item.toValue < 0 || item.fromValue < 0) {
          this.toastError(`From and To values cannot be empty or negative at ${type} slab: ${item.slabTitle}`);
        }
        if (item.toValue < item.fromValue) {
          this.toastError(`'From' cannot be greater than 'To' at ${type} slab: ${item.slabTitle}`);
        }
        if (!Number.isInteger(item.toValue) || !Number.isInteger(item.fromValue)) {
          this.toastError(`Non integer values are not allowed at ${type} slab: ${item.slabTitle}`);
        }
        if (!item.minPerAmount || item.minPerAmount < 0 || isNaN(item.minPerAmount)) {
          this.toastError(`Per KM/Per Order is either empty or not a positive-integer value at ${type} slab: ${item.slabTitle}`);
        }
        if (item.bonus < 0 || !Number.isInteger(item.bonus)) {
          this.toastError(`Bonus is negative or a non-integer value at ${type} slab: ${item.slabTitle}`);
        }
      });
    }
  }

  private validateCategoryForSlab(slabArray: any, type: string) {
    if (slabArray && slabArray.slabData && slabArray.slabData.slabs.length > 0 && !slabArray.slabData.category) {
      this.toastError(`Category not provided for ${type} slab`);
    }
  }

  private validateTimeAndSlabData(slabArray: any, type: string) {
    if (slabArray &&
        slabArray.timeData.length === 0 && slabArray.slabData.slabs.length > 0) {
      this.toastError(`Time data is mandatory for ${type} slab`);
    }
  }

  private deleteSlabDataIfEmpty(slabArray: any, type: string) {
    if (slabArray && slabArray.slabData && slabArray.slabData.slabs.length === 0) {
      this.toasterService.showToaster(new Toaster({ type: ToasterType.NOTIFICATION, message: `No ${type} data` }));
      delete this.finalSlabData[type];
    }
  }

  private validateDuplicateTimeIntervals(slabArray: any, type: string) {
    if (slabArray && slabArray.timeData && slabArray.timeData.length > 0) {
      const times: any[] = [];
      slabArray.timeData = slabArray.timeData.reduce((acc: any[], item: any) => {
        times.push({ time: item.fromTime }, { time: item.toTime });
        acc.push({ fromTime: item.fromTime, toTime: item.toTime });
        return acc;
      }, []);
      if (this.checkDuplicateInObject('time', times)) {
        this.toastError(`Duplicate or blank values found for time data in ${type} Slab. Please fix.`);
      }
    }
  }

  private validateCommonFields() {
    this.loadingIndicator = true;
    this.params.payrollVersion = 2.0;
    this.finalSlabData = this.params;
    this.persistValues = JSON.parse(JSON.stringify(this.params));

    if (!this.selectedCities || this.selectedCities.length === 0) {
      this.toastError('City is missing');
    }
    if (!this.params.serviceTypes || this.params.serviceTypes.length === 0) {
      this.toastError('Service(s) is missing');
    }
    this.selectedServiceNames = (this.params.serviceTypes || []).map((id: string) => this.serviceNames[id]);

    if (!this.isGeoSlabPage && (!this.params.shift || this.params.shift.length === 0)) {
      this.toastError('Shift(s) is missing');
    }
    if (this.isGeoSlabPage && !this.selectedGeoLayerType) {
      this.toastError('Please select a layer');
    }
    if (this.isGeoSlabPage && !this.selectedLayer) {
      this.toastError('Please select a ' + this.getGeoLayerTypeDisplayName().toLowerCase());
    }
    if (this.isGeoSlabPage) {
      if (this.geoFenceRadius == null || this.geoFenceRadius === ('' as any) || isNaN(Number(this.geoFenceRadius)) || Number(this.geoFenceRadius) <= 0) {
        this.toastError('Please enter a valid radius greater than 0');
      }
      if (this.geoFenceAmount == null || this.geoFenceAmount === ('' as any) || isNaN(Number(this.geoFenceAmount)) || Number(this.geoFenceAmount) < 0) {
        this.toastError('Please enter a valid amount');
      }
    }
    if (!this.isGeoSlabPage && this.isMinimumGuaranteeEarningModel() && this.getSelectedShiftCount() > 1) {
      this.toastError(
        'Minimum Guarantee earning model allows only one shift. Remove extra shifts before creating this slab.'
      );
    }
    if (!this.params.startDate) {
      this.toastError('Start date is missing');
    }
    if (this.params.startDate && moment(this.params.startDate, 'YYYY-MM-DD') < moment().startOf('day')) {
      this.toastError('Start date cannot be in the past');
    }
    this.params.startDate = this.formatDate(this.params.startDate);
    delete this.params.endDate;
  }

  private validateCategorySelected() {
    if (!this.category) {
      this.toastError('Please select a category for all slabs');
    }
  }

  private validateFistMileFields() {
    if (!this.isEmpty(this.thresholdFareFM) && this.isEmpty(this.thresholdDistanceFM)) {
      this.toastError('Please fill first mile threshold distance');
    }
    if (this.isEmpty(this.thresholdFareFM) && !this.isEmpty(this.thresholdDistanceFM)) {
      this.toastError('Please fill last mile threshold Fare');
    }
    if (!this.isEmpty(this.thresholdDistanceFM) || !this.isEmpty(this.thresholdFareFM)) {
      if (this.isEmpty(this.maxCompensationFM) || this.maxCompensationFM < 0 || this.maxCompensationFM > 99) {
        this.toastError('Please enter a positive Max Compensation less than 100');
      }
      if (this.isEmpty(this.minCompensationFM) || this.minCompensationFM < 0 || this.minCompensationFM > 99) {
        this.toastError('Please enter a positive Min Compensation less than 100');
      }
      if (this.minCompensationFM > this.maxCompensationFM) {
        this.toastError('The minimum compensation amount should be lesser than the maximum FM compensation amount');
      }
      if (this.minCompensationFM > this.thresholdFareFM) {
        this.toastError('The minimum compensation amount should be lesser than the FM threshold amount');
      }
    }
  }

  private validateLastMileFields() {
    if (!this.isEmpty(this.thresholdFareLM) && this.isEmpty(this.thresholdDistanceLM)) {
      this.toastError('Please fill last mile threshold distance');
    }
  }

  private validateCommission() {
    this.reconcileCommissionFields();
    if (
      !this.showPercentCommission() &&
      !this.showRupeesCommission()
    ) {
      return;
    }
    if (
      this.actionTitle === 'Add' ||
      (this.actionTitle === 'Edit' &&
        ((this.category === 'order' && !this.isBatchedOrder) || this.category !== 'order'))
    ) {
      if (!this.params.commissionType) {
        this.toastError('Please select a commission type');
      }
      if (this.params.commissionType === 'percent') {
        this.params.commissionOnBonus = true;
      }
      if (this.isEmpty(this.params.commissionAmount)) {
        this.toastError('Please enter a commission amount');
      }
      if (!this.isEmpty(this.params.commissionAmount) && this.params.commissionAmount < 0) {
        this.toastError('Commission amount cannot be negative');
      }
    }

    if (
      this.category === 'order' &&
      ((this.actionTitle === 'Add' || this.actionTitle === 'Duplicate') ||
       (this.actionTitle === 'Edit' && this.isBatchedOrder === true))
    ) {
      if (!this.params.commissionTypeBatchedFirstOrder) {
        this.toastError('Please select a commission type for first batched order');
      }
      if (!this.params.commissionTypeBatchedNextOrder) {
        this.toastError('Please select a commission type for next batched order');
      }
      if (!this.BatchedFirstOrderCategory) {
        this.toastError('Please select a category for batched first order slab');
      }
      if (!this.BatchedNextOrderCategory) {
        this.toastError('Please select a category for batched next order slab');
      }
      if (this.params.commissionTypeBatchedFirstOrder === 'percent') {
        this.params.commissionOnBonusBatchedFirstOrder = true;
      }
      if (this.params.commissionTypeBatchedNextOrder === 'percent') {
        this.params.commissionOnBonusBatchedNextOrder = true;
      }
      if (this.isEmpty(this.params.commissionAmountBatchedFirstOrder)) {
        this.toastError('Please enter a commission amount for batched first order');
      }
      if (this.isEmpty(this.params.commissionAmountBatchedNextOrder)) {
        this.toastError('Please enter a commission amount for batched next order');
      }
      if (!this.isEmpty(this.params.commissionAmountBatchedFirstOrder) && this.params.commissionAmountBatchedFirstOrder < 0) {
        this.toastError('Commission amount for Batched first order cannot be negative');
      }
      if (!this.isEmpty(this.params.commissionAmountBatchedNextOrder) && this.params.commissionAmountBatchedNextOrder < 0) {
        this.toastError('Commission amount for Batched next order cannot be negative');
      }
    }
  }

  private validateSlabs() {
    if (this.category === 'cm') {
      this.params.peakSlab = {
        slabData: {
          category: 'cm',
          slabs: [{ slabTitle: 'slab-0', slabType: 'cm', minPerAmount: 0, toValue: 9999, fromValue: 0 }]
        },
        timeData: [{ toTime: '23:59', fromTime: '00:00' }]
      };
      delete this.params.nonPeakSlab;
      delete this.params.flexibleSlab;
      delete this.params.nightSlab;

    } else if (
      (this.category === 'order' || this.category === 'distance') &&
      ((this.actionTitle === 'Add' || this.actionTitle === 'Duplicate') || this.isBatchedOrder === false)
    ) {
      if (this.category === 'distance') {
        delete this.params.nonPeakSlab;
        delete this.params.flexibleSlab;
        delete this.params.nightSlab;
      }

      const slabTypes = this.category === 'distance'
        ? ['peakSlab']
        : ['peakSlab', 'nonPeakSlab', 'flexibleSlab', 'nightSlab'];
      slabTypes.forEach(slabType => {
        if (this.params[slabType] && this.params[slabType].slabData && this.params[slabType].slabData.slabs) {
          this.params[slabType].slabData.slabs.forEach((slab: any, index: number) => {
            if (slab.slabTitle === undefined || slab.slabTitle === null || slab.slabTitle === '') {
              slab.slabTitle = index;
            }
            slab.slabTitle = `${slab.slabTitle}`;
            if (!slab.slabTitle.includes('slab-')) {
              slab.slabTitle = `slab-${slab.slabTitle}`;
            }
          });
        }
        this.timeDataValidation(this.params[slabType]);
        this.slabDataValidation(this.params[slabType]);
        this.removeEmptyTimeData(this.params[slabType]);
        this.validateForEmptyValues(this.params[slabType], slabType);
        this.validateCategoryForSlab(this.params[slabType], slabType);
        this.validateDuplicateTimeIntervals(this.params[slabType], slabType);
        this.validateTimeAndSlabData(this.params[slabType], slabType);
        this.deleteSlabDataIfEmpty(this.params[slabType], slabType);
        this.fixTimes(this.params[slabType]);
      });
    }

    if (
      this.category === 'order' &&
      ((this.actionTitle === 'Add' || this.actionTitle === 'Duplicate') || this.isBatchedOrder === true)
    ) {
      const batchedTypes = [
        'peakSlabBatchedFirstOrder', 'nonPeakSlabBatchedFirstOrder',
        'flexibleSlabBatchedFirstOrder', 'nightSlabBatchedFirstOrder',
        'peakSlabBatchedNextOrder', 'nonPeakSlabBatchedNextOrder',
        'flexibleSlabBatchedNextOrder', 'nightSlabBatchedNextOrder'
      ];
      batchedTypes.forEach(slabType => {
        if (this.params[slabType] && this.params[slabType].slabData && this.params[slabType].slabData.slabs) {
          this.params[slabType].slabData.slabs.forEach((slab: any, index: number) => {
            if (slab.slabTitle === undefined || slab.slabTitle === null || slab.slabTitle === '') {
              slab.slabTitle = index;
            }
            slab.slabTitle = `${slab.slabTitle}`;
            if (!slab.slabTitle.includes('slab-')) {
              slab.slabTitle = `slab-${slab.slabTitle}`;
            }
          });
        }
        this.timeDataValidation(this.params[slabType]);
        this.slabDataValidation(this.params[slabType]);
        this.removeEmptyTimeData(this.params[slabType]);
        this.validateForEmptyValues(this.params[slabType], slabType);
        this.validateCategoryForSlab(this.params[slabType], slabType);
        this.validateDuplicateTimeIntervals(this.params[slabType], slabType);
        this.validateTimeAndSlabData(this.params[slabType], slabType);
        this.deleteSlabDataIfEmpty(this.params[slabType], slabType);
        this.fixTimes(this.params[slabType]);
      });
    }

    if (
      this.category === 'order' &&
      ((this.actionTitle === 'Add' || this.actionTitle === 'Duplicate') ||
       (this.actionTitle === 'Edit' && this.isBatchedOrder === true))
    ) {
      if (
        this.isEmpty(this.params.peakSlabBatchedFirstOrder) &&
        this.isEmpty(this.params.nonPeakSlabBatchedFirstOrder) &&
        this.isEmpty(this.params.flexibleSlabBatchedFirstOrder) &&
        this.isEmpty(this.params.nightSlabBatchedFirstOrder)
      ) {
        this.toastError('Please fill in the batch order slab details');
      }
      if (
        this.isEmpty(this.params.peakSlabBatchedNextOrder) &&
        this.isEmpty(this.params.nonPeakSlabBatchedNextOrder) &&
        this.isEmpty(this.params.flexibleSlabBatchedNextOrder) &&
        this.isEmpty(this.params.nightSlabBatchedNextOrder)
      ) {
        this.toastError('Please fill in the batch order slab details');
      }

      this.params.batchDetails = {};

      const batchedFirstOrder: any = {
        peakSlab:           this.params.peakSlabBatchedFirstOrder,
        nonPeakSlab:        this.params.nonPeakSlabBatchedFirstOrder,
        flexibleSlab:       this.params.flexibleSlabBatchedFirstOrder,
        nightSlab:          this.params.nightSlabBatchedFirstOrder,
        commissionAmount:   this.params.commissionAmountBatchedFirstOrder.toString(),
        commissionType:     this.params.commissionTypeBatchedFirstOrder,
        commissionOnBonus:  this.params.commissionOnBonusBatchedFirstOrder,
        thresholdDistanceFM: this.thresholdDistanceFM,
        thresholdDistanceLM: this.thresholdDistanceLM,
        thresholdFareFM:    this.thresholdFareFM,
        thresholdFareLM:    this.thresholdFareLM,
        maxCompensationFM:  this.maxCompensationFM,
        minCompensationFM:  this.minCompensationFM,
        isSurgeIncluded:    true
      };
      this.params.batchDetails.firstOrder = batchedFirstOrder;
      ['peakSlabBatchedFirstOrder', 'nonPeakSlabBatchedFirstOrder', 'flexibleSlabBatchedFirstOrder',
       'nightSlabBatchedFirstOrder', 'commissionAmountBatchedFirstOrder', 'commissionTypeBatchedFirstOrder',
       'commissionOnBonusBatchedFirstOrder'].forEach(k => delete this.params[k]);

      const batchedNextOrder: any = {
        peakSlab:          this.params.peakSlabBatchedNextOrder,
        nonPeakSlab:       this.params.nonPeakSlabBatchedNextOrder,
        flexibleSlab:      this.params.flexibleSlabBatchedNextOrder,
        nightSlab:         this.params.nightSlabBatchedNextOrder,
        commissionAmount:  this.params.commissionAmountBatchedNextOrder.toString(),
        commissionType:    this.params.commissionTypeBatchedNextOrder,
        commissionOnBonus: this.params.commissionOnBonusBatchedNextOrder,
        thresholdDistanceLM: this.thresholdDistanceLM,
        thresholdFareLM:   this.thresholdFareLM,
        isSurgeIncluded:   true
      };
      this.params.batchDetails.nextOrder = batchedNextOrder;
      ['peakSlabBatchedNextOrder', 'nonPeakSlabBatchedNextOrder', 'flexibleSlabBatchedNextOrder',
       'nightSlabBatchedNextOrder', 'commissionAmountBatchedNextOrder', 'commissionTypeBatchedNextOrder',
       'commissionOnBonusBatchedNextOrder'].forEach(k => delete this.params[k]);
    }

    // Mirrors Rapido Dashboard UI: if((actionTitle=='Add'||actionTitle=='Duplicate') || (!isBatchedOrder))
    if ((this.actionTitle === 'Add' || this.actionTitle === 'Duplicate') || !this.isBatchedOrder) {
      if (this.category === 'distance') {
        // For distance category, only peak slab is required
        if (this.isEmpty(this.params.peakSlab)) {
          this.toastError('Please fill in the slab details');
        }
      } else {
        // For order category, check all slabs
        if (
          this.isEmpty(this.params.peakSlab) && this.isEmpty(this.params.nonPeakSlab) &&
          this.isEmpty(this.params.flexibleSlab) && this.isEmpty(this.params.nightSlab)
        ) {
          this.toastError('Please fill in the slab details');
        }
      }
    }
  }

  submitSlab() {
    try {
      this.validateCommonFields();
      this.validateCategorySelected();
      this.validateEarningModel();
      this.validateFistMileFields();
      this.validateLastMileFields();
      this.validateMinimumGuaranteeDetails();
      this.validatePoolSlab();

      // Copy timeBasedSlabs into params for distance category
      if (this.category === 'distance') {
        if (this.timeBasedSlabs.length > 0) {
          this.params.timeBasedSlabs = this.timeBasedSlabs.map((tb: any) => {
            // Ensure times are in 24-hour format before processing
            const normalizedStartTime = this.convertTo24Hour(tb.startTime || '');
            const normalizedEndTime = this.convertTo24Hour(tb.endTime || '');

            return {
              ...tb,
              startTime: normalizedStartTime ? (normalizedStartTime.length === 5 ? normalizedStartTime + ':00' : normalizedStartTime) : normalizedStartTime,
              endTime:   normalizedEndTime   ? (normalizedEndTime.length   === 5 ? normalizedEndTime   + ':59' : normalizedEndTime)   : normalizedEndTime
            };
          });
        } else {
          this.params.timeBasedSlabs = undefined;
        }
      } else {
        delete this.params.timeBasedSlabs;
        delete this.params.minimumPayout;
      }

      if (this.canSetDesiredCustomerPpkm && this.desiredCustomerPpkm != null) {
        this.params.desiredCustomerPpkm = this.desiredCustomerPpkm;
      } else {
        delete this.params.desiredCustomerPpkm;
      }

      // Clean up first/last mile if empty
      if (this.isEmpty(this.thresholdFareFM) && this.isEmpty(this.thresholdDistanceFM)) {
        this.thresholdFareFM = null;
        this.thresholdDistanceFM = null;
        this.minCompensationFM = null;
        this.maxCompensationFM = null;
      }
      if (this.isEmpty(this.thresholdFareLM) && this.isEmpty(this.thresholdDistanceLM)) {
        this.thresholdFareLM = null;
        this.thresholdDistanceLM = null;
      }

      this.params.active = true;
      this.params.thresholdDistanceFM = this.thresholdDistanceFM;
      this.params.thresholdFareFM = this.thresholdFareFM;
      this.params.maxCompensationFM = this.maxCompensationFM;
      this.params.minCompensationFM = this.minCompensationFM;
      // Only include last-mile fields for 'order' category (not for cm or distance)
      if (this.category === 'order') {
        this.params.thresholdDistanceLM = this.thresholdDistanceLM;
        this.params.thresholdFareLM = this.thresholdFareLM;
      } else {
        delete this.params.thresholdDistanceLM;
        delete this.params.thresholdFareLM;
      }
      this.params.excludeIncentiveTypes = (this.params.excludeIncentiveTypes || [])
        .filter((t: string) => t && t.trim() !== '');
      this.params.isSurgeIncluded = true;
      // Always hardcode all days
      this.params.days = this.daysList.slice();

      this.validateCommission();
      this.validateFlatCommissionDeductions();
      this.validateSlabs();

      this.params.cities = [this.selectedCities];
      this.loadingIndicator = false;
      if (this.isGeoSlabPage) {
        this.lookupExistingGeoSlab().then(() => {
          this.showConfirmationModal = true;
        }).catch(() => {});
        return;
      }
      this.showConfirmationModal = true;
    } catch (e) {
      this.loadingIndicator = false;
    }
  }

  private createTwoSlabs(): any[] {
    if (this.category === 'order') {
      const nonBatchedSlab = JSON.parse(JSON.stringify(this.finalSlabData));
      delete nonBatchedSlab.batchDetails;
      nonBatchedSlab.batchedOrder = false;

      const batchedSlab = JSON.parse(JSON.stringify(this.finalSlabData));
      batchedSlab.batchedOrder = true;
      this.deleteUnusedValues(batchedSlab);

      return [batchedSlab, nonBatchedSlab].map(slab => this.toPoolDetailsPayload(slab));
    }
    return [this.toPoolDetailsPayload(JSON.parse(JSON.stringify(this.finalSlabData)))];
  }

  // captain-earnings expects pool config as poolDetails { size, compensationMultipliers }
  // (CreatePoolDetails); the flat poolSize/poolMultipliers fields are UI-only.
  private toPoolDetailsPayload(slab: any): any {
    if (this.isPoolServiceSelected() && slab.poolSize >= 2) {
      slab.poolDetails = {
        size: slab.poolSize,
        compensationMultipliers: Utils.normalizePoolMultipliers(slab.poolMultipliers),
      };
    }
    delete slab.poolSize;
    delete slab.poolMultipliers;
    return slab;
  }

  private deleteUnusedValues(slab: any) {
    ['commissionAmount', 'commissionType', 'commissionOnBonus',
     'peakSlab', 'nonPeakSlab', 'flexibleSlab', 'nightSlab',
     'thresholdDistanceFM', 'thresholdDistanceLM', 'thresholdFareFM',
     'thresholdFareLM', 'isSurgeIncluded', 'maxCompensationFM', 'minCompensationFM']
      .forEach(k => delete slab[k]);
  }

  async saveData() {
    if (!this.isGeoSlabPage && !Array.isArray(this.params.shift)) {
      this.params.shift = [this.params.shift];
    }
    if (!this.isBatchedOrder && this.params.commissionAmount != null) {
      this.params.commissionAmount = this.params.commissionAmount.toString();
    }

    // Add createdBy field to the payload
    let userEmail = '';
    try {
      const user: any = await this.authService.getUserDetails().toPromise();
      userEmail = (user && user.email) ? user.email : '';
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
    this.finalSlabData.createdBy = userEmail;

    this.loadingIndicator = true;
    const twoSlabs = this.createTwoSlabs();
    if (this.isGeoSlabPage) {
      twoSlabs.forEach((slab: any) => this.applyGeoSlabPayloadFields(slab, userEmail));
    }

    this.sharedService.createSlab(twoSlabs).toPromise()
      .then(() => {
        this.loadingIndicator = false;
        this.showConfirmationModal = false;
        this.toasterService.showToaster(new Toaster({
          type: ToasterType.SUCCESS,
          message: this.isGeoSlabPage
            ? (this.existingGeoSlab
              ? 'Existing config for this hotspot and city is updated with the latest slab.'
              : 'Geo Slab successfully created')
            : 'Slab successfully created'
        }));
        this.router.navigate(['/slabs']);
      })
      .catch((error: any) => {
        this.loadingIndicator = false;
        this.showConfirmationModal = false;
        const msg = (error && error.error && error.error.message) || (error && error.message) || 'Error creating slab';
        this.showError(msg);
      });
  }

  cancelConfirmation() {
    const types = [
      'peakSlab', 'nonPeakSlab', 'flexibleSlab', 'nightSlab',
      'peakSlabBatchedFirstOrder', 'nonPeakSlabBatchedFirstOrder',
      'flexibleSlabBatchedFirstOrder', 'nightSlabBatchedFirstOrder',
      'peakSlabBatchedNextOrder', 'nonPeakSlabBatchedNextOrder',
      'flexibleSlabBatchedNextOrder', 'nightSlabBatchedNextOrder'
    ];
    if (this.persistValues) {
      types.forEach(t => {
        if (this.persistValues[t] && this.persistValues[t].timeData) {
          this.persistValues[t].timeData.forEach((td: any) => {
            delete td.timeFrom;
            delete td.fromTime;
            delete td.toTime;
          });
        }
      });
      this.params = this.persistValues;
    }
    this.showConfirmationModal = false;
  }

  filteredServices(): any[] {
    if (!this.existingServiceSearch) { return this.services; }
    const query = this.existingServiceSearch.toLowerCase();
    return this.services.filter(s => s.service && s.service.name && s.service.name.toLowerCase().includes(query));
  }

  filteredShifts(): string[] {
    if (!this.existingShiftSearch) { return this.shifts; }
    const query = this.existingShiftSearch.toLowerCase();
    return this.shifts.filter(s => s.toLowerCase().includes(query));
  }

  // Utility functions

  private formatDate(date: any): string {
    if (!date) { return date; }
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private showError(msg: string) {
    this.toasterService.showToaster(new Toaster({
      type: ToasterType.WARNING,
      message: msg,
      delay: 5000,
      config: { dismissible: true }
    }));
  }

  getViewModeDisplayText(): string {
    if (this.selectedModeNames && this.selectedModeNames.length > 0) {
      return this.selectedModeNames.join(', ');
    }
    if (this.selectedMode && this.modes && this.modes.length) {
      const match = this.modes.find((m: any) => String(m.id) === String(this.selectedMode));
      if (match && match.name) {
        return match.name;
      }
    }
    if (this.selectedMode) {
      return this.selectedMode;
    }
    return '';
  }

  private toastError(msg: string) {
    this.loadingIndicator = false;
    this.showError(msg);
    throw new Error(msg);
  }

  private isMinimumGuaranteeEarningModel(): boolean {
    return this.params && this.params.earningModel === 'minimum_guarantee';
  }

  get minimumGuaranteeFlowLoadingMessage(): string | null {
    if (!this.isMinimumGuaranteeEarningModel()) {
      return null;
    }
    if (this.minimumGuaranteeSlabsLookupPending) {
      return 'MG details are loading, please wait.';
    }
    if (this.microCorridorsLoading) {
      return 'MC details are loading, please wait.';
    }
    return null;
  }

  private getSelectedShiftCount(): number {
    const s = this.params && this.params.shift;
    if (s == null || s === '') {
      return 0;
    }
    if (Array.isArray(s)) {
      return s.filter((x: any) => x != null && String(x).trim() !== '').length;
    }
    return String(s).trim() === '' ? 0 : 1;
  }

  private warnIfMinimumGuaranteeWithMultipleShifts(): void {
    if (!this.isMinimumGuaranteeEarningModel() || this.isViewMode) {
      return;
    }
    if (this.getSelectedShiftCount() > 1) {
      this.showError(
        'Minimum Guarantee allows only one shift. Remove extra shifts before creating this slab.'
      );
    }
  }

  // Pool Slab helpers
  /**
   * True only when all selected services share the same orderType and that
   * orderType is configured for pool slabs (dashboard config poolSlabOrderTypes).
   */
  isPoolServiceSelected(): boolean {
    const poolOrderTypes = (this.dashboardConfig && this.dashboardConfig.poolSlabOrderTypes) || [];
    const selected: string[] = this.params.serviceTypes || [];
    if (selected.length === 0) {
      return false;
    }
    const orderTypes = selected.map(id => {
      const entry = this.serviceMap[id];
      return entry && entry.orderType;
    });
    const first = orderTypes[0];
    if (!first || poolOrderTypes.indexOf(first) === -1) {
      return false;
    }
    return orderTypes.every(orderType => orderType === first);
  }

  showPoolFields(): boolean {
    if (this.isViewMode) {
      return this.params.poolSize != null;
    }
    return this.isPoolServiceSelected();
  }

  // Mirrors captain-earnings validatePoolSlab (internal/services/slab/validate.go)
  private validatePoolSlab(): void {
    if (!this.isPoolServiceSelected()) {
      return;
    }
    if (!this.params.poolSize || this.params.poolSize < 2) {
      this.toastError('Pool size must be at least 2 for pool slabs');
    }
    const multipliers = Utils.normalizePoolMultipliers(this.params.poolMultipliers);
    Object.keys(multipliers).forEach(position => {
      const pos = parseInt(position, 10);
      if (isNaN(pos) || pos < 2 || pos > this.params.poolSize) {
        this.toastError(`Pool multiplier slot ${position} must be between 2 and pool size ${this.params.poolSize}`);
      }
      const value = multipliers[position];
      if (value == null || isNaN(value) || value < 0 || value > 1) {
        this.toastError(`Pool multiplier for position ${position} must be between 0% and 100%`);
      }
    });
  }

  onPoolSizeChange(): void {
    if (!this.params.poolSize || this.params.poolSize < 2) {
      this.params.poolMultipliers = {};
      return;
    }
    // Drop multipliers for slot positions outside 2..poolSize
    const multipliers = Utils.normalizePoolMultipliers(this.params.poolMultipliers);
    Object.keys(multipliers).forEach(position => {
      const pos = parseInt(position, 10);
      if (isNaN(pos) || pos < 2 || pos > this.params.poolSize) {
        delete multipliers[position];
      }
    });
    this.params.poolMultipliers = multipliers;
  }

  getPoolMultiplierPositions(): number[] {
    if (!this.params.poolSize || this.params.poolSize < 2) {
      return [];
    }
    // Return array [2, 3, 4, ...] up to poolSize
    const positions: number[] = [];
    for (let i = 2; i <= this.params.poolSize; i++) {
      positions.push(i);
    }
    return positions;
  }

  getPoolMultiplierValue(position: number): number | null {
    const multipliers = this.params.poolMultipliers;
    if (multipliers == null || Array.isArray(multipliers)) {
      return null;
    }
    const value = multipliers[String(position)];
    // Convert decimal multiplier (0.75) to percentage (75) for display; null when unset
    return value != null ? value * 100 : null;
  }

  setPoolMultiplierValue(position: number, value: any): void {
    if (this.params.poolMultipliers == null || Array.isArray(this.params.poolMultipliers)) {
      this.params.poolMultipliers = {};
    }
    // Convert string to number (from input event) and then percentage to decimal (75 -> 0.75).
    // Empty/invalid input unsets the slot instead of forcing a 0% multiplier.
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (numValue == null || isNaN(numValue)) {
      delete this.params.poolMultipliers[String(position)];
      return;
    }
    this.params.poolMultipliers[String(position)] = numValue / 100;
  }

  private getPrimarySelectedShift(): string | null {
    const selectedShift = this.params && this.params.shift;
    if (Array.isArray(selectedShift)) {
      return selectedShift.length > 0 ? String(selectedShift[0]) : null;
    }
    if (typeof selectedShift === 'string' && selectedShift.trim() !== '') {
      return selectedShift;
    }
    return null;
  }

  get minimumGuaranteeViewSlabsQueryParams(): { shift?: string; earningModel: string } {
    const shift = this.getPrimarySelectedShift();
    const out: { shift?: string; earningModel: string } = { earningModel: 'minimum_guarantee' };
    if (shift) {
      out.shift = shift;
    }
    return out;
  }

  openMinimumGuaranteeSlabsInNewWindow(event: Event): void {
    if (event) {
      event.preventDefault();
    }
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/slabs'], { queryParams: this.minimumGuaranteeViewSlabsQueryParams })
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  isMinimumGuaranteeSlotTimeLocked(): boolean {
    return this.isViewMode || this.isMinimumGuaranteeReadOnly || this.selectedMicroCorridorIds.length > 0;
  }

  isMicroCorridorSelectionDisabled(): boolean {
    return this.isViewMode || this.isMinimumGuaranteeReadOnly;
  }

  get showMinimumGuaranteeMicroCorridorField(): boolean {
    if (!this.params || this.params.earningModel !== 'minimum_guarantee') {
      return false;
    }
    if (this.isViewMode) {
      const d = this.params.minimumGuaranteeDetails;
      return !!(d && d.microCorridorId);
    }
    const dMg = this.params.minimumGuaranteeDetails;
    if (this.isMinimumGuaranteeReadOnly) {
      const hasLockedMc = !!(dMg && dMg.microCorridorId);
      if (!this.minimumGuaranteeShiftRequiresMicroCorridorId && !hasLockedMc) {
        return false;
      }
    }
    if (
      this.microCorridors.length > 0
      || this.minimumGuaranteeShiftRequiresMicroCorridorId
      || this.microCorridorsLoading
      || this.microCorridorsError
      || (this.isMinimumGuaranteeReadOnly && !!(dMg && dMg.microCorridorId))
    ) {
      return true;
    }
    if (this.actionTitle === 'Duplicate') {
      const d = this.params.minimumGuaranteeDetails;
      return !!(d && d.microCorridorId);
    }
    return false;
  }

  /**
   * ng-select with `[multiple]="true"` — model is an array; only one MicroCorridor is allowed (latest wins).
   */
  onMicroCorridorSelectionChange(ids: string[] | null | undefined): void {
    this.ensureMinimumGuaranteeDetailsObject(false);
    let arr = Array.isArray(ids) ? ids.filter((id) => id != null && String(id).trim() !== '') : [];
    if (arr.length > 1) {
      arr = [arr[arr.length - 1]];
    }
    this.selectedMicroCorridorIds = arr.slice();
    this.applySelectedMicroCorridorToMinimumGuarantee();
  }

  retryMicroCorridorLookup(): void {
    this.microCorridorsError = false;
    this.microCorridorsErrorCanRetry = true;
    this.microCorridorsErrorMessage = this.defaultMicroCorridorsErrorMessage;
    this.evaluateMicroCorridorState(true);
  }

  private applySelectedMicroCorridorToMinimumGuarantee(): void {
    if (!this.isMinimumGuaranteeEarningModel()) {
      return;
    }
    this.ensureMinimumGuaranteeDetailsObject(false);
    const selectedMcId = this.selectedMicroCorridorIds.length > 0
      ? String(this.selectedMicroCorridorIds[0])
      : null;
    if (!selectedMcId) {
      this.params.minimumGuaranteeDetails.microCorridorId = null;
      this.params.minimumGuaranteeDetails.slots = this.normalizeSlotCount([
        { startTime: '', endTime: '' }
      ]);
      return;
    }
    const selectedMicroCorridor = this.microCorridors.find(
      (mc) => String(mc.id) === selectedMcId || String(mc.zoneId) === selectedMcId
    );
    if (!selectedMicroCorridor) {
      this.params.minimumGuaranteeDetails.microCorridorId = null;
      this.params.minimumGuaranteeDetails.slots = this.normalizeSlotCount([
        { startTime: '', endTime: '' }
      ]);
      return;
    }

    this.params.minimumGuaranteeDetails.microCorridorId = selectedMicroCorridor.id;
    this.params.minimumGuaranteeDetails.slots = [{
      startTime: selectedMicroCorridor.startTime,
      endTime: selectedMicroCorridor.endTime
    }];
  }

  private buildMicroCorridorContextKey(): string | null {
    if (!this.isMinimumGuaranteeEarningModel()) { return null; }
    const city = this.selectedCities || '';
    const serviceTypes = Array.isArray(this.params.serviceTypes)
      ? this.params.serviceTypes.filter((sd: string) => !!sd).slice().sort()
      : [];
    if (!city || serviceTypes.length === 0) {
      return null;
    }
    return `${city}::${serviceTypes.join(',')}`;
  }

  private resetMicroCorridorState(clearSelection: boolean): void {
    this.microCorridors = [];
    this.microCorridorsLoading = false;
    this.microCorridorsError = false;
    this.microCorridorsErrorCanRetry = true;
    this.microCorridorsErrorMessage = this.defaultMicroCorridorsErrorMessage;
    this.currentMicroCorridorContextKey = null;
    this.syncMinimumGuaranteeLoadingOverlay();
    if (!clearSelection) {
      return;
    }
    this.selectedMicroCorridorIds = [];
    if (this.params && this.params.minimumGuaranteeDetails) {
      this.params.minimumGuaranteeDetails.microCorridorId = null;
    }
  }

  private evaluateMicroCorridorState(forceRefresh: boolean = false): void {
    if (!this.isMinimumGuaranteeEarningModel()) {
      this.resetMicroCorridorState(true);
      return;
    }
    this.ensureMinimumGuaranteeDetailsObject(false);
    if (!forceRefresh && this.isMinimumGuaranteeReadOnly && !this.params.minimumGuaranteeDetails.microCorridorId) {
      this.resetMicroCorridorState(true);
      return;
    }
    if (this.selectedMicroCorridorIds.length === 0 && this.params.minimumGuaranteeDetails.microCorridorId) {
      this.selectedMicroCorridorIds = [String(this.params.minimumGuaranteeDetails.microCorridorId)];
    }
    const contextKey = this.buildMicroCorridorContextKey();
    if (!contextKey) {
      this.resetMicroCorridorState(true);
      return;
    }
    if (forceRefresh) {
      delete this.microCorridorLookupCache[contextKey];
    }
    if (!forceRefresh && this.currentMicroCorridorContextKey === contextKey && this.microCorridors.length > 0) {
      return;
    }
    if (!forceRefresh && this.microCorridorLookupCache[contextKey]) {
      this.currentMicroCorridorContextKey = contextKey;
      this.microCorridors = this.microCorridorLookupCache[contextKey].slice();
      this.microCorridorsLoading = false;
      this.microCorridorsError = false;
      this.reconcileMicroCorridorSelectionWithOptions();
      return;
    }
    if (this.microCorridorLookupInFlight[contextKey]) {
      return;
    }
    this.currentMicroCorridorContextKey = contextKey;
    this.fetchMicroCorridorsForCurrentContext(contextKey);
  }

  private extractMicroCorridorSlotTime(enabledTimeSlots: any[], key: 'start' | 'end'): string {
    if (!Array.isArray(enabledTimeSlots) || enabledTimeSlots.length === 0) {
      return '';
    }
    const firstTimeSlot = enabledTimeSlots[0] || {};
    const durations = Array.isArray(firstTimeSlot.duration) ? firstTimeSlot.duration : [];
    if (durations.length === 0) {
      return '';
    }
    const raw = durations[0] && durations[0][key] != null ? String(durations[0][key]) : '';
    if (!raw) {
      return '';
    }
    if (raw.length === 4 && /^\d{4}$/.test(raw)) {
      return `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;
    }
    return this.convertTo24Hour(raw);
  }

  private normalizeMicroCorridorOption(rawMicroCorridor: any): MicroCorridorOption | null {
    if (!rawMicroCorridor || typeof rawMicroCorridor !== 'object') {
      return null;
    }
    const zoneIdCandidate = rawMicroCorridor.zoneId != null ? rawMicroCorridor.zoneId : rawMicroCorridor.id;
    if (!zoneIdCandidate) {
      return null;
    }
    const startTimeRaw = rawMicroCorridor.startTime || this.extractMicroCorridorSlotTime(rawMicroCorridor.enabledTimeSlots, 'start');
    const endTimeRaw = rawMicroCorridor.endTime || this.extractMicroCorridorSlotTime(rawMicroCorridor.enabledTimeSlots, 'end');

    return {
      id: rawMicroCorridor.id != null ? String(rawMicroCorridor.id) : String(zoneIdCandidate),
      name: rawMicroCorridor.name ? String(rawMicroCorridor.name) : String(zoneIdCandidate),
      zoneId: String(zoneIdCandidate),
      startTime: this.convertTo24Hour(startTimeRaw || ''),
      endTime: this.convertTo24Hour(endTimeRaw || '')
    };
  }

  private fetchMicroCorridorsForCurrentContext(contextKey: string): void {
    const cityObj = this.cities.find(c => c.displayName === this.selectedCities);
    const serviceDetailIds = Array.isArray(this.params.serviceTypes)
      ? this.params.serviceTypes.filter((sd: string) => !!sd)
      : [];
    if (!cityObj || serviceDetailIds.length === 0) {
      this.resetMicroCorridorState(true);
      return;
    }

    this.microCorridorsLoading = true;
    this.microCorridorsError = false;
    this.microCorridorsErrorCanRetry = true;
    this.microCorridorsErrorMessage = this.defaultMicroCorridorsErrorMessage;
    this.syncMinimumGuaranteeLoadingOverlay();
    this.microCorridorLookupInFlight[contextKey] = true;
    const payload = {
      cityId: cityObj._id,
      serviceDetailIds
    };

    this.sharedService.fetchMicroCorridors(payload).toPromise()
      .then((res: any) => {
        if (!this.isMinimumGuaranteeEarningModel() || this.currentMicroCorridorContextKey !== contextKey) {
          return;
        }
        const rawMicroCorridors = (res && Array.isArray(res.microCorridors))
          ? res.microCorridors
          : ((res && res.data && Array.isArray(res.data.microCorridors)) ? res.data.microCorridors : []);
        const mapped = rawMicroCorridors
          .map((mc: any) => this.normalizeMicroCorridorOption(mc))
          .filter((mc: MicroCorridorOption | null) => !!mc) as MicroCorridorOption[];
        this.microCorridors = mapped.filter((mc: MicroCorridorOption) => !!mc.name);
        if (this.microCorridors.length === 0) {
          this.selectedMicroCorridorIds = [];
          this.params.minimumGuaranteeDetails.microCorridorId = null;
          this.microCorridorsError = true;
          this.microCorridorsErrorCanRetry = false;
          this.microCorridorsErrorMessage = this.microCorridorsEmptyErrorMessage;
          delete this.microCorridorLookupCache[contextKey];
          return;
        }
        this.microCorridorLookupCache[contextKey] = this.microCorridors.slice();
        this.reconcileMicroCorridorSelectionWithOptions();
      })
      .catch(() => {
        if (!this.isMinimumGuaranteeEarningModel() || this.currentMicroCorridorContextKey !== contextKey) {
          return;
        }
        this.microCorridors = [];
        this.selectedMicroCorridorIds = [];
        this.params.minimumGuaranteeDetails.microCorridorId = null;
        this.microCorridorsError = true;
        this.microCorridorsErrorCanRetry = true;
        this.microCorridorsErrorMessage = this.defaultMicroCorridorsErrorMessage;
        delete this.microCorridorLookupCache[contextKey];
      })
      .finally(() => {
        delete this.microCorridorLookupInFlight[contextKey];
        if (this.currentMicroCorridorContextKey === contextKey) {
          this.microCorridorsLoading = false;
          this.syncMinimumGuaranteeLoadingOverlay();
        }
      });
  }

  private reconcileMicroCorridorSelectionWithOptions(): void {
    if (this.microCorridors.length === 0) {
      this.selectedMicroCorridorIds = [];
      this.params.minimumGuaranteeDetails.microCorridorId = null;
      return;
    }
    const activeSelection = (this.selectedMicroCorridorIds.length > 0
      ? this.selectedMicroCorridorIds[0]
      : null) || this.params.minimumGuaranteeDetails.microCorridorId;
    if (activeSelection) {
      const matched = this.microCorridors.find(
        (mc) => String(mc.id) === String(activeSelection) || String(mc.zoneId) === String(activeSelection)
      );
      if (matched) {
        this.selectedMicroCorridorIds = [matched.id];
        this.applySelectedMicroCorridorToMinimumGuarantee();
        return;
      }
    }
    if (!this.isMinimumGuaranteeReadOnly) {
      this.selectedMicroCorridorIds = [];
      this.params.minimumGuaranteeDetails.microCorridorId = null;
    }
  }

  private buildMinimumGuaranteeContextKey(): string | null {
    if (!this.isMinimumGuaranteeEarningModel()) { return null; }
    const city = this.selectedCities || '';
    const mode = this.selectedMode || this.mode || '';
    const shift = this.getPrimarySelectedShift();
    const serviceTypes = Array.isArray(this.params.serviceTypes)
      ? this.params.serviceTypes.slice().sort()
      : [];
    if (!city || !mode || !shift || serviceTypes.length === 0) { return null; }
    return `${city}::${mode}::${shift}::${serviceTypes.join(',')}`;
  }

  private ensureMinimumGuaranteeDetailsObject(resetValues: boolean): void {
    if (!this.isMinimumGuaranteeEarningModel()) {
      delete this.params.minimumGuaranteeDetails;
      return;
    }
    if (resetValues || !this.params.minimumGuaranteeDetails || typeof this.params.minimumGuaranteeDetails !== 'object') {
      this.params.minimumGuaranteeDetails = {
        eligibilityCriteria: { orders: null },
        minimumGuaranteeAmount: null,
        perceptionAmount: null,
        slots: this.buildMinimumGuaranteeSlots(),
        microCorridorId: null
      } as MinimumGuaranteeDetails;
      return;
    }
    const mgDetails = this.params.minimumGuaranteeDetails;
    if (!mgDetails.eligibilityCriteria || typeof mgDetails.eligibilityCriteria !== 'object') {
      mgDetails.eligibilityCriteria = { orders: null };
    }
    mgDetails.slots = this.normalizeSlotCount(mgDetails.slots || []);
    if (mgDetails.microCorridorId == null || mgDetails.microCorridorId === '') {
      mgDetails.microCorridorId = null;
    } else {
      mgDetails.microCorridorId = String(mgDetails.microCorridorId);
    }
  }

  private applyMinimumGuaranteeFromLoadedSlabDocument(): void {
    const raw = this.params.minimumGuaranteeDetails;
    const normalized = this.normalizeMinimumGuaranteeDetailsFromSlab(raw);
    if (normalized) {
      this.params.minimumGuaranteeDetails = JSON.parse(JSON.stringify(normalized));
    } else if (raw && typeof raw === 'object') {
      this.params.minimumGuaranteeDetails = JSON.parse(JSON.stringify(raw));
      this.ensureMinimumGuaranteeDetailsObject(false);
    } else {
      delete this.params.minimumGuaranteeDetails;
      this.ensureMinimumGuaranteeDetailsObject(true);
    }
    if (this.params.minimumGuaranteeDetails) {
      this.params.minimumGuaranteeDetails.slots = this.normalizeSlotCount(
        this.params.minimumGuaranteeDetails.slots || []
      );
      this.selectedMicroCorridorIds = this.params.minimumGuaranteeDetails.microCorridorId
        ? [String(this.params.minimumGuaranteeDetails.microCorridorId)]
        : [];
    }
    this.isMinimumGuaranteeReadOnly = this.isViewMode;
    this.showMinimumGuaranteeBanner = false;
    this.currentMinimumGuaranteeContextKey = this.buildMinimumGuaranteeContextKey();
    this.minimumGuaranteeSlabsLookupPending = false;
    this.minimumGuaranteeSlabsLookupError = false;
    this.syncMinimumGuaranteeLoadingOverlay();
  }

  private evaluateMinimumGuaranteeState(forceContextRefresh: boolean = false): void {
    if (!this.isMinimumGuaranteeEarningModel()) {
      delete this.params.minimumGuaranteeDetails;
      this.isMinimumGuaranteeReadOnly = false;
      this.showMinimumGuaranteeBanner = false;
      this.currentMinimumGuaranteeContextKey = null;
      this.minimumGuaranteeSlabsLookupPending = false;
      this.minimumGuaranteeSlabsLookupError = false;
      this.minimumGuaranteeShiftRequiresMicroCorridorId = false;
      this.resetMicroCorridorState(true);
      this.syncMinimumGuaranteeLoadingOverlay();
      return;
    }

    if (this.isViewMode) {
      this.applyMinimumGuaranteeFromLoadedSlabDocument();
      this.evaluateMicroCorridorState(false);
      return;
    }

    if (this.actionTitle === 'Duplicate' && !forceContextRefresh) {
      this.applyMinimumGuaranteeFromLoadedSlabDocument();
      this.evaluateMicroCorridorState(false);
      this.refreshMinimumGuaranteeShiftMicroCorridorRequirement();
      return;
    }

    const contextKey = this.buildMinimumGuaranteeContextKey();
    if (!contextKey) {
      this.currentMinimumGuaranteeContextKey = null;
      this.isMinimumGuaranteeReadOnly = false;
      this.showMinimumGuaranteeBanner = false;
      delete this.params.minimumGuaranteeDetails;
      this.ensureMinimumGuaranteeDetailsObject(true);
      this.minimumGuaranteeSlabsLookupPending = false;
      this.minimumGuaranteeSlabsLookupError = false;
      this.minimumGuaranteeShiftRequiresMicroCorridorId = false;
      this.resetMicroCorridorState(true);
      this.syncMinimumGuaranteeLoadingOverlay();
      return;
    }

    const contextChanged = forceContextRefresh || this.currentMinimumGuaranteeContextKey !== contextKey;
    this.currentMinimumGuaranteeContextKey = contextKey;
    if (contextChanged) {
      this.showMinimumGuaranteeBanner = false;
    }

    if (forceContextRefresh) {
      delete this.minimumGuaranteeLookupCache[contextKey];
    }
    const cachedResult = this.minimumGuaranteeLookupCache[contextKey];
    if (cachedResult) {
      this.minimumGuaranteeSlabsLookupPending = false;
      this.minimumGuaranteeSlabsLookupError = false;
      this.syncMinimumGuaranteeLoadingOverlay();
      this.applyMinimumGuaranteeLookupResult(cachedResult, contextKey);
      this.evaluateMicroCorridorState(false);
      return;
    }

    if (this.minimumGuaranteeLookupInFlight[contextKey]) {
      return;
    }
    this.minimumGuaranteeLookupInFlight[contextKey] = true;
    this.minimumGuaranteeSlabsLookupError = false;
    this.minimumGuaranteeSlabsLookupPending = true;
    this.syncMinimumGuaranteeLoadingOverlay();

    const lookupContextKey = contextKey;
    this.fetchMinimumGuaranteeDetailsForCurrentContext()
      .then((lookupResult) => {
        this.minimumGuaranteeLookupCache[lookupContextKey] = lookupResult;
        if (this.currentMinimumGuaranteeContextKey !== lookupContextKey || !this.isMinimumGuaranteeEarningModel()) {
          return;
        }
        this.minimumGuaranteeSlabsLookupError = false;
        this.applyMinimumGuaranteeLookupResult(lookupResult, lookupContextKey);
        this.evaluateMicroCorridorState(false);
      })
      .catch(() => {
        if (this.currentMinimumGuaranteeContextKey !== lookupContextKey || !this.isMinimumGuaranteeEarningModel()) {
          return;
        }
        this.minimumGuaranteeSlabsLookupError = true;
        delete this.params.minimumGuaranteeDetails;
        this.isMinimumGuaranteeReadOnly = false;
        this.showMinimumGuaranteeBanner = false;
        this.loadingIndicator = false;
        this.minimumGuaranteeShiftRequiresMicroCorridorId = false;
        this.resetMicroCorridorState(true);
        this.showError(this.minimumGuaranteeSlabsLookupErrorMessage);
      })
      .finally(() => {
        delete this.minimumGuaranteeLookupInFlight[lookupContextKey];
        if (this.currentMinimumGuaranteeContextKey === lookupContextKey && this.isMinimumGuaranteeEarningModel()) {
          this.minimumGuaranteeSlabsLookupPending = false;
          this.syncMinimumGuaranteeLoadingOverlay();
        }
      });
  }

  retryMinimumGuaranteeSlabsLookup(): void {
    if (!this.isMinimumGuaranteeEarningModel()) {
      return;
    }
    const contextKey = this.buildMinimumGuaranteeContextKey();
    if (contextKey) {
      delete this.minimumGuaranteeLookupCache[contextKey];
    }
    this.minimumGuaranteeSlabsLookupError = false;
    this.evaluateMinimumGuaranteeState(true);
  }

  private fetchMinimumGuaranteeDetailsForCurrentContext(): Promise<{
    hasConstruct: boolean,
    details: MinimumGuaranteeDetails | null,
    shiftRequiresMicroCorridorIdForMg: boolean
  }> {
    const selectedShift = this.getPrimarySelectedShift();
    if (!selectedShift) {
      return Promise.resolve({
        hasConstruct: false,
        details: null,
        shiftRequiresMicroCorridorIdForMg: false
      });
    }
    const payload = {
      limit: 100,
      offset: 0,
      filters: {
        cities: this.selectedCities ? [this.selectedCities] : [],
        shifts: [selectedShift],
        active: true,
        earningModel: ['minimum_guarantee'],
      },
      responseParams: {
        requiredFields: ['earningModel', 'minimumGuaranteeDetails'],
      },
    };
    return this.sharedService.fetchSlabs(payload).toPromise()
      .then((res: any) => {
        const slabs: any[] = res && Array.isArray(res.slabs) ? res.slabs: [];
        const minimumGuaranteeDetails = this.extractMinimumGuaranteeDetailsFromSlabs(slabs, selectedShift);
        const shiftRequiresMicroCorridorIdForMg =
          this.anyMinimumGuaranteeSlabOnShiftHasMicroCorridorId(slabs, selectedShift);
        return {
          hasConstruct: !!minimumGuaranteeDetails,
          details: minimumGuaranteeDetails,
          shiftRequiresMicroCorridorIdForMg
        };
      });
  }

  private slabShiftMatchesSelectedShift(slab: any, selectedShift: string): boolean {
    const slabShift = slab && slab.shift;
    return Array.isArray(slabShift)
      ? slabShift.some((s: any) => String(s) === String(selectedShift))
      : String(slabShift || '') === String(selectedShift);
  }

  private anyMinimumGuaranteeSlabOnShiftHasMicroCorridorId(slabs: any[], selectedShift: string): boolean {
    if (slabs.length === 0) {
      return false;
    }
    for (const slab of slabs) {
      if (!this.slabShiftMatchesSelectedShift(slab, selectedShift)) {
        continue;
      }
      if (!slab || slab.earningModel !== 'minimum_guarantee') {
        continue;
      }
      const rawMg = slab.minimumGuaranteeDetails;
      if (!rawMg) {
        continue;
      }
      if (rawMg.microCorridorId) {
        return true;
      }
    }
    return false;
  }

  /** Duplicate / late binding: shift policy when MG lookup short-circuits without slab list. */
  private refreshMinimumGuaranteeShiftMicroCorridorRequirement(): void {
    const selectedShift = this.getPrimarySelectedShift();
    if (!selectedShift || !this.selectedCities || !this.isMinimumGuaranteeEarningModel()) {
      this.minimumGuaranteeShiftRequiresMicroCorridorId = false;
      return;
    }
    const payload = {
      limit: 100,
      offset: 0,
      filters: {
        cities: this.selectedCities ? [this.selectedCities] : [],
        shifts: [selectedShift],
        active: true,
        earningModel: ['minimum_guarantee'],
      },
      responseParams: {
        requiredFields: ['minimumGuaranteeDetails', 'earningModel'],
      },
    };
    this.sharedService.fetchSlabs(payload).toPromise()
      .then((res: any) => {
        const slabs: any[] = res && Array.isArray(res.slabs) ? res.slabs: [];
        const fromShift = this.anyMinimumGuaranteeSlabOnShiftHasMicroCorridorId(slabs, selectedShift);
        const fromLoaded = !!(this.params.minimumGuaranteeDetails
          && this.params.minimumGuaranteeDetails.microCorridorId);
        this.minimumGuaranteeShiftRequiresMicroCorridorId = fromShift || fromLoaded;
      })
      .catch(() => {
        this.minimumGuaranteeShiftRequiresMicroCorridorId = false;
      });
  }

  private extractMinimumGuaranteeDetailsFromSlabs(
    slabs: any[],
    selectedShift: string
  ): MinimumGuaranteeDetails | null {
    if (!Array.isArray(slabs)) { return null; }
    for (const slab of slabs) {
      if (!this.slabShiftMatchesSelectedShift(slab, selectedShift)) { continue; }

      const details = this.normalizeMinimumGuaranteeDetailsFromSlab(slab && slab.minimumGuaranteeDetails);
      if (details) {
        return details;
      }
    }
    return null;
  }


  private normalizeMinimumGuaranteeDetailsFromSlab(rawDetails: any): MinimumGuaranteeDetails | null {
    if (!rawDetails || typeof rawDetails !== 'object') {
      return null;
    }
    const orders = rawDetails.eligibilityCriteria && rawDetails.eligibilityCriteria.orders;
    const minimumGuaranteeAmount = rawDetails.minimumGuaranteeAmount;
    const perceptionAmount = rawDetails.perceptionAmount;
    const allSlots = this.normalizeSlotCount(Array.isArray(rawDetails.slots) ? rawDetails.slots : []);
    const slots = allSlots
      .filter((slot: any) => slot && slot.startTime && slot.endTime)
      .map((slot: any) => ({
        startTime: this.convertTo24Hour(slot.startTime),
        endTime: this.convertTo24Hour(slot.endTime)
      }));

    const hasValidOrders = orders != null && orders !== '' && !isNaN(Number(orders));
    const hasValidMinimumGuaranteeAmount =
      minimumGuaranteeAmount != null && minimumGuaranteeAmount !== '' && !isNaN(Number(minimumGuaranteeAmount));
    const hasValidPerceptionAmount =
      (perceptionAmount == null || perceptionAmount === '' || !isNaN(Number(perceptionAmount)));
    if (!hasValidOrders || !hasValidMinimumGuaranteeAmount || !hasValidPerceptionAmount || slots.length === 0) {
      return null;
    }
    const normalizedPerceptionAmount = perceptionAmount == null || perceptionAmount === ''
      ? null
      : Number(perceptionAmount);
    return {
      eligibilityCriteria: {
        orders: Number(orders)
      },
      minimumGuaranteeAmount: Number(minimumGuaranteeAmount),
      perceptionAmount: normalizedPerceptionAmount,
      slots,
      microCorridorId: rawDetails.microCorridorId
    };
  }

  private collectMinimumGuaranteeValidationErrors(): string[] {
    const errors: string[] = [];
    const d = this.params.minimumGuaranteeDetails;
    if (!d || typeof d !== 'object') {
      return ['Minimum Guarantee details are missing.'];
    }
    const maxSlots = this.minimumGuaranteeSlotCount;
    const slots = Array.isArray(d.slots) ? d.slots : [];
    if (slots.length > maxSlots) {
      errors.push(
        `You cannot have more than ${maxSlots} Minimum Guarantee time slot${maxSlots === 1 ? '' : 's'} ` +
        `(configured maximum).`
      );
    }
    if (slots.length < 1) {
      errors.push('At least one Minimum Guarantee time slot is required.');
    }

    const orders = d.eligibilityCriteria && d.eligibilityCriteria.orders;
    if (orders == null || orders === '' || isNaN(Number(orders))) {
      errors.push('MG Eligibility Orders is required and must be a valid number.');
    }

    const mga = d.minimumGuaranteeAmount;
    if (mga == null || mga === '' || isNaN(Number(mga))) {
      errors.push('Minimum Guarantee Amount is required and must be a valid number.');
    }

    const per = d.perceptionAmount;
    if (per != null && per !== '' && isNaN(Number(per))) {
      errors.push('Perception Amount must be a valid number when provided.');
    }

    if (
      !this.microCorridorsLoading
      && this.minimumGuaranteeShiftRequiresMicroCorridorId
      && !d.microCorridorId
    ) {
      errors.push(
        'MicroCorridor ID is required: an existing Minimum Guarantee slab for this shift includes a MicroCorridor. ' +
        'Select one when the list loads, or retry if the list is empty.'
      );
    }

    for (let i = 0; i < slots.length; i += 1) {
      const slot = slots[i];
      const hasStart = !!(slot && slot.startTime);
      const hasEnd = !!(slot && slot.endTime);
      if (!hasStart && !hasEnd) {
        errors.push(`Slot ${i + 1}: start time and end time are required.`);
      } else if (!hasStart) {
        errors.push(`Slot ${i + 1}: start time is required.`);
      } else if (!hasEnd) {
        errors.push(`Slot ${i + 1}: end time is required.`);
      }
    }

    return errors;
  }

  private normalizeMinimumGuaranteeDetailsForSave(rawDetails: any): MinimumGuaranteeDetails | null {
    if (!rawDetails || typeof rawDetails !== 'object') {
      return null;
    }
    const orders = rawDetails.eligibilityCriteria && rawDetails.eligibilityCriteria.orders;
    const minimumGuaranteeAmount = rawDetails.minimumGuaranteeAmount;
    const perceptionAmount = rawDetails.perceptionAmount;
    const allSlots = this.normalizeSlotCount(Array.isArray(rawDetails.slots) ? rawDetails.slots : []);
    const slots = allSlots
      .filter((slot: any) => slot && slot.startTime && slot.endTime)
      .map((slot: any) => ({
        startTime: this.convertTo24Hour(slot.startTime),
        endTime: this.convertTo24Hour(slot.endTime)
      }));
    const normalizedPerceptionAmount = perceptionAmount == null || perceptionAmount === ''
      ? null
      : Number(perceptionAmount);
    return {
      eligibilityCriteria: {
        orders: Number(orders)
      },
      minimumGuaranteeAmount: Number(minimumGuaranteeAmount),
      perceptionAmount: normalizedPerceptionAmount,
      slots,
      microCorridorId: rawDetails.microCorridorId
    };
  }

  private applyMinimumGuaranteeLookupResult(
    lookupResult: {
      hasConstruct: boolean,
      details: MinimumGuaranteeDetails | null,
      shiftRequiresMicroCorridorIdForMg?: boolean
    },
    contextKey: string
  ): void {
    if (this.currentMinimumGuaranteeContextKey !== contextKey || !this.isMinimumGuaranteeEarningModel()) {
      return;
    }
    this.minimumGuaranteeShiftRequiresMicroCorridorId = !!lookupResult.shiftRequiresMicroCorridorIdForMg;
    if (lookupResult.hasConstruct && lookupResult.details) {
      this.params.minimumGuaranteeDetails = JSON.parse(JSON.stringify(lookupResult.details));
      this.params.minimumGuaranteeDetails.slots = this.normalizeSlotCount(
        this.params.minimumGuaranteeDetails.slots || []
      );
      this.selectedMicroCorridorIds = this.params.minimumGuaranteeDetails.microCorridorId
        ? [String(this.params.minimumGuaranteeDetails.microCorridorId)]
        : [];
      this.isMinimumGuaranteeReadOnly = true;
      this.showMinimumGuaranteeBanner = !this.minimumGuaranteeBannerDismissedByContext[contextKey];
      return;
    }
    delete this.params.minimumGuaranteeDetails;
    this.ensureMinimumGuaranteeDetailsObject(true);
    this.selectedMicroCorridorIds = [];
    this.isMinimumGuaranteeReadOnly = false;
    this.showMinimumGuaranteeBanner = false;
  }

  private validateMinimumGuaranteeDetails(): void {
    if (!this.isMinimumGuaranteeEarningModel()) {
      delete this.params.minimumGuaranteeDetails;
      return;
    }
    const validationErrors = this.collectMinimumGuaranteeValidationErrors();
    if (validationErrors.length > 0) {
      this.toastError(validationErrors.join(' '));
    }
    const normalizedDetails = this.normalizeMinimumGuaranteeDetailsForSave(this.params.minimumGuaranteeDetails);
    if (!normalizedDetails || !normalizedDetails.slots || normalizedDetails.slots.length === 0) {
      this.toastError('Minimum Guarantee time slots could not be saved. Please check slot times.');
    }
    this.params.minimumGuaranteeDetails = normalizedDetails;
  }

  private parseMinimumGuaranteeSlotCount(rawCount: any): number {
    const parsed = Number(rawCount);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return 1;
    }
    return Math.floor(parsed);
  }

  private buildMinimumGuaranteeSlots(): MinimumGuaranteeSlot[] {
    return [{ startTime: '', endTime: '' }];
  }

  private getSafeMinimumGuaranteeSlotLimit(currentSlotCount: number): number {
    const configuredSlotCount = Number(this.minimumGuaranteeSlotCount);

    if (!Number.isFinite(configuredSlotCount) || configuredSlotCount < 1) {
      return currentSlotCount;
    }

    const normalizedConfiguredSlotCount = Math.floor(configuredSlotCount);

    if (normalizedConfiguredSlotCount === 1 && currentSlotCount > 1) {
      return currentSlotCount;
    }

    return normalizedConfiguredSlotCount;
  }

  private normalizeSlotCount(rawSlots: any[]): MinimumGuaranteeSlot[] {
    const normalized = (rawSlots || []).map((slot: any) => ({
      startTime: slot && slot.startTime ? this.convertTo24Hour(slot.startTime) : '',
      endTime: slot && slot.endTime ? this.convertTo24Hour(slot.endTime) : ''
    }));
    if (normalized.length === 0) {
      return [{ startTime: '', endTime: '' }];
    }
    const maxSlots = this.getSafeMinimumGuaranteeSlotLimit(normalized.length);
    return normalized.slice(0, maxSlots);
  }

  private syncMinimumGuaranteeSlotsInState(): void {
    if (!this.params || !this.params.minimumGuaranteeDetails) { return; }
    this.params.minimumGuaranteeDetails.slots = this.normalizeSlotCount(
      this.params.minimumGuaranteeDetails.slots || []
    );
  }
}
